import React, { useEffect, useState } from 'react';
import api from '../api';
import toast from 'react-hot-toast';
import { useConfirm } from '../context/ConfirmContext';
import io from 'socket.io-client';
import { QRCodeCanvas } from 'qrcode.react';
import { BASE_URL } from '../config';
import {
    Shield, Save, Users, Clock, AlertTriangle,
    Ticket, Plus, Trash2, RefreshCw, Calendar, CreditCard,
    Megaphone, Search, Play, MessageSquare, Mail,
    Send, StopCircle, CheckCircle, MessageCircle, Smartphone,
    Sparkles, TrendingUp, Key, Zap, LogOut, CheckSquare, Square,
    AlertCircle, X
} from 'lucide-react';

const socket = io(BASE_URL, {
    transports: ['websocket', 'polling']
});

const USER_EMAIL_TEMPLATES = [
    {
        id: 'unverified_activation',
        title: '🚀 Ativação (Conta sem WhatsApp)',
        subject: '🚀 Complete seu cadastro e comece a automatizar seus achadinhos!',
        text: `Olá {nome}!\n\nNotamos que você se cadastrou no Anúncios App, mas ainda não confirmou o seu número de WhatsApp.\n\nPara que o robô envie ofertas automáticas para os seus grupos da Shopee, Mercado Livre e Amazon, basta confirmar seu WhatsApp ou Telegram no painel:\n\n👉 Acesse agora: https://anunciosapp.com.br\n\nQualquer dúvida, conte com nossa equipe de suporte!`
    },
    {
        id: 'onboarding_email',
        title: '💡 Primeiros Passos & Dicas',
        subject: '💡 Como lucrar com ofertas automáticas nos seus grupos',
        text: `Olá {nome}!\n\nSeja muito bem-vindo ao Anúncios App! 🚀\n\nSabia que afiliados que automatizam o envio de achadinhos vendem até 5x mais?\n\n1️⃣ Conecte seu WhatsApp ou Telegram\n2️⃣ Configure sua tag de afiliado\n3️⃣ O robô envia promoções imperdíveis no piloto automático!\n\n👉 Acesse seu painel: https://anunciosapp.com.br`
    },
    {
        id: 'vip_offer',
        title: '🎟️ Condição Especial de Upgrade',
        subject: '🎟️ Condição exclusiva para liberar grupos ilimitados no Anúncios App',
        text: `Olá {nome}!\n\nTemos uma condição exclusiva para você turbinar suas vendas e gerenciar grupos ilimitados.\n\n👉 Confira seu plano e ative o upgrade: https://anunciosapp.com.br`
    }
];

const USER_TEMPLATES = [
    {
        id: 'onboarding',
        title: '🚀 Boas-vindas & Passo a Passo',
        text: `Olá {nome}! Tudo bem? 🚀\n\nVi que você criou sua conta no Anúncios App! Você já conseguiu conectar seu grupo de ofertas?\n\nÉ muito simples:\n1️⃣ Conecte seu WhatsApp ou Telegram no painel\n2️⃣ Cadastre o link do seu grupo\n3️⃣ Escolha os horários e deixe o robô postar os melhores achadinhos no piloto automático!\n\n👉 Acesse seu painel: https://anunciosapp.com.br\n\nSe tiver qualquer dúvida, responda esta mensagem que nossa equipe te ajuda!`
    },
    {
        id: 'sales_tips',
        title: '💰 Dica de Afiliado (Multiplicar Vendas)',
        text: `Oi {nome}, tudo bem? 💡\n\nVocê sabia que afiliados que automatizam o envio de achadinhos vendem até 5x mais?\n\nCom o Anúncios App, você não perde tempo garimpando links. O robô converte sua tag da Shopee e Mercado Livre e envia nos seus grupos mesmo enquanto você dorme.\n\n👉 Aproveite seu plano {plano} e configure agora: https://anunciosapp.com.br`
    },
    {
        id: 'support_help',
        title: '🤝 Ajuda do Suporte Técnico',
        text: `Olá {nome}, aqui é do suporte do Anúncios App! 👋\n\nNotei que seu robô ainda não começou a disparar nos seus grupos. Teve alguma dificuldade na configuração ou quer que a gente te ajude passo a passo?\n\nResponda aqui que te auxilio agora mesmo!`
    },
    {
        id: 'special_offer',
        title: '🎟️ Oferta Especial com Desconto',
        text: `Olá {nome}! 🎉\n\nPreparamos uma condição exclusiva para você turbinar suas vendas com o plano ilimitado do Anúncios App!\n\nUse um cupom especial e ganhe desconto na sua assinatura.\n\n👉 Acesse e ative seu desconto: https://anunciosapp.com.br`
    }
];

const DEFAULT_BROADCAST_TEMPLATE = `🚀 *PARE DE PERDER TEMPO CONVERTENDO LINKS MANUALMENTE!*

Conheça a ferramenta definitiva para afiliados Shopee, Mercado Livre e outras plataformas! Esqueça o trabalho manual de procurar produtos e converter links. O nosso robô faz tudo por você:

✅ Envio automático 24 horas por dia, 7 dias por semana dos melhores achadinhos do momento
✅ Conversão instantânea de links com a sua tag de afiliado
✅ Multiplataforma: Envia direto no WhatsApp e Telegram
✅ Automação no Instagram: Responde comentários no Direct automaticamente
✅ Painel Inteligente com gerenciamento de cupons e relatórios

👉 Acesse agora e crie sua conta grátis: https://anunciosapp.shop/
(Comece a escalar suas comissões hoje mesmo!)`;

const DEFAULT_WEEKLY_MSG_UPGRADE = `Fala, *{nome}*! Tudo bem? 🚀\n\nNotamos que suas credenciais de afiliado já estão configuradas no *Anúncios App*! 👏\n\nPorém, no seu plano atual (*Grátis*), o bot fica limitado a apenas 1 grupo e com postagens automáticas desativadas. Ou seja: enquanto você dorme ou trabalha, você deixa de lucrar com achadinhos em alta!\n\nNos planos *Basic* e *Premium*, você desbloqueia:\n✅ *Disparo 100% no Piloto Automático (24/7)*\n✅ Múltiplos grupos e canais simultâneos\n✅ Resposta automática no Direct do Instagram e Telegram\n\n👉 Dê o próximo passo e comece a escalar suas comissões hoje: {link_upgrade}\n\nSe precisar de qualquer ajuda para ativar o piloto automático, é só me responder aqui! 😉`;

const DEFAULT_WEEKLY_MSG_ACTIVATION = `Olá, *{nome}*! Tudo certo?\n\nSeu WhatsApp já está confirmado no *Anúncios App*, mas vimos que você ainda não adicionou seu ID de afiliado (Shopee, Mercado Livre ou Amazon).\n\nSem o seu ID, o robô não tem como colocar o seu link de comissão nas ofertas!\n\nConfigurar leva *menos de 2 minutos* e você já pode começar a faturar no seu plano Grátis:\n1️⃣ Acesse seu painel: {link_config}\n2️⃣ Vá em *Configurações > Plataformas* e cole seu ID de afiliado.\n3️⃣ Salve e comece a divulgar com comissão automática!\n\nTravou em alguma etapa? É só me responder aqui que eu te ajudo na hora! 👊`;


const AdminPage = ({ userPlan, initialTab = 'users', onTabChange }) => {
    const confirm = useConfirm();
    const [activeTab, setActiveTabState] = useState(initialTab);

    useEffect(() => {
        if (initialTab) {
            setActiveTabState(prev => (prev !== initialTab ? initialTab : prev));
        }
    }, [initialTab]);

    const setActiveTab = (tab) => {
        setActiveTabState(tab);
        if (onTabChange) onTabChange(tab);
    };

    const [config, setConfig] = useState({
        limiteGruposFree: 1,
        limiteGruposBasic: 5,
        cooldownTrocaGrupoSegundos: 3600,
        cooldownDisparoFreeSegundos: 86400,
        priceBasic: 49.90,
        pricePremium: 99.90,
        priceDivulgadorBasic: 39.90,
        priceDivulgadorPremium: 79.90,
        landingWhatsappNumber: '',
        defaultTrialDays: 7,
        emailServiceActive: true,
        whatsappSystemActive: true,
        emailWelcomeActive: true
    });
    const [isLoading, setIsLoading] = useState(false);

    // Coupon management states
    const [coupons, setCoupons] = useState([]);
    const [loadingCoupons, setLoadingCoupons] = useState(false);
    const [newCoupon, setNewCoupon] = useState({
        code: '',
        discountPercentage: '',
        onlyFirstSubscription: false,
        allowedPlans: [], // 'basic', 'premium'
        expirationDate: '',
        maxUses: '',
        duration: 'once' // 'once' | 'forever'
    });

    // Broadcast states (Grupos e Campanhas)
    const [broadcastSubTab, setBroadcastSubTab] = useState('weekly'); // 'weekly' | 'users' | 'groups' | 'email'
    
    // Email Broadcast states
    const [emailBroadcastSubject, setEmailBroadcastSubject] = useState(USER_EMAIL_TEMPLATES[0].subject);
    const [emailBroadcastMessage, setEmailBroadcastMessage] = useState(USER_EMAIL_TEMPLATES[0].text);
    const [emailBroadcastFilter, setEmailBroadcastFilter] = useState('unverified_whatsapp');
    const [emailBroadcastTestEmail, setEmailBroadcastTestEmail] = useState('');
    const [emailBroadcastStatus, setEmailBroadcastStatus] = useState(null);
    const [emailBroadcastLogs, setEmailBroadcastLogs] = useState([]);
    const [sendingEmailBroadcast, setSendingEmailBroadcast] = useState(false);
    const [loadingEmailLogs, setLoadingEmailLogs] = useState(false);
    const [testingEmailMessage, setTestingEmailMessage] = useState(false);
    
    // Weekly Campaign states (Ativação & Upgrade)
    const [weeklyTab, setWeeklyTab] = useState('upgrade'); // 'upgrade' | 'activation'
    const [weeklyMsgUpgrade, setWeeklyMsgUpgrade] = useState(DEFAULT_WEEKLY_MSG_UPGRADE);
    const [weeklyMsgActivation, setWeeklyMsgActivation] = useState(DEFAULT_WEEKLY_MSG_ACTIVATION);
    const [weeklyStats, setWeeklyStats] = useState(null);
    const [loadingWeeklyStats, setLoadingWeeklyStats] = useState(false);
    const [savingWeeklyConfig, setSavingWeeklyConfig] = useState(false);
    const [weeklyTestNumber, setWeeklyTestNumber] = useState('');
    const [sendingWeeklyTest, setSendingWeeklyTest] = useState(false);
    const [sendingWeeklyCampaign, setSendingWeeklyCampaign] = useState(false);
    const [weeklyMinDelay, setWeeklyMinDelay] = useState(8);
    const [weeklyMaxDelay, setWeeklyMaxDelay] = useState(15);
    const [broadcastGroups, setBroadcastGroups] = useState([]);
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [searchGroupQuery, setSearchGroupQuery] = useState('');
    const [broadcastMessage, setBroadcastMessage] = useState(DEFAULT_BROADCAST_TEMPLATE);
    const [broadcastInstance, setBroadcastInstance] = useState('A');
    const [broadcastStatus, setBroadcastStatus] = useState(null);
    const [broadcastLogs, setBroadcastLogs] = useState([]);
    const [loadingGroups, setLoadingGroups] = useState(false);
    const [loadingLogs, setLoadingLogs] = useState(false);
    const [sendingBroadcast, setSendingBroadcast] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    // User messaging states (Broadcast Usuários Cadastrados)
    const [userBroadcastMessage, setUserBroadcastMessage] = useState(USER_TEMPLATES[0].text);
    const [userFilterPlan, setUserFilterPlan] = useState('all');
    const [userFilterAudience, setUserFilterAudience] = useState('all');
    const [userMinDelay, setUserMinDelay] = useState(8);
    const [userMaxDelay, setUserMaxDelay] = useState(15);
    const [userTestNumber, setUserTestNumber] = useState('');
    const [userBroadcastStatus, setUserBroadcastStatus] = useState(null);
    const [userBroadcastLogs, setUserBroadcastLogs] = useState([]);
    const [sendingUserBroadcast, setSendingUserBroadcast] = useState(false);
    const [loadingUserLogs, setLoadingUserLogs] = useState(false);
    const [testingUserMessage, setTestingUserMessage] = useState(false);

    // WhatsApp Instances states (Instâncias Oficiais A e B)
    const [qrCodeA, setQrCodeA] = useState('');
    const [whatsStatusA, setWhatsStatusA] = useState('Iniciando...');
    const [qrCodeB, setQrCodeB] = useState('');
    const [whatsStatusB, setWhatsStatusB] = useState('Iniciando...');
    const [whatsappMarketingActive, setWhatsappMarketingActive] = useState(true);
    const [whatsappAutoReplyA, setWhatsappAutoReplyA] = useState(false);
    const [aiPromptA, setAiPromptA] = useState('');
    const [whatsappAutoReplyB, setWhatsappAutoReplyB] = useState(false);
    const [aiPromptB, setAiPromptB] = useState('');
    const [postarNoStatus, setPostarNoStatus] = useState(false);
    const [savingInstanceConfig, setSavingInstanceConfig] = useState(false);

    // User messaging selection & filters (Disparo Avulso)
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [userSearchTerm, setUserSearchTerm] = useState('');
    const [userPhoneFilter, setUserPhoneFilter] = useState('with_phone'); // 'with_phone' | 'all' | 'without_phone'

    // Admin Users states
    const [adminUsers, setAdminUsers] = useState([]);
    const [loadingAdminUsers, setLoadingAdminUsers] = useState(false);
    const [showEditPlanModal, setShowEditPlanModal] = useState(false);
    const [editPlanData, setEditPlanData] = useState({ userId: null, username: '', plan: 'free', trialDays: '', role: 'user', initialRole: 'user' });

    const fetchAdminUsers = async () => {
        setLoadingAdminUsers(true);
        try {
            const res = await api.get('/auth/admin/users');
            setAdminUsers(res.data || []);
        } catch (err) {
            console.error("Erro ao carregar usuários admin", err);
            toast.error("Erro ao carregar lista de usuários.");
        } finally {
            setLoadingAdminUsers(false);
        }
    };

    const handleUpdateUserPlan = async (e) => {
        e.preventDefault();
        const loadToast = toast.loading("Atualizando dados do usuário...");
        try {
            await api.post(`/auth/admin/users/${editPlanData.userId}/plan`, {
                plan: editPlanData.plan,
                trialDays: editPlanData.trialDays
            });

            if (userPlan === 'admin' && editPlanData.role && editPlanData.role !== editPlanData.initialRole) {
                await api.post(`/auth/admin/users/${editPlanData.userId}/role`, {
                    role: editPlanData.role
                });
            }

            toast.success("Usuário atualizado com sucesso!", { id: loadToast });
            setShowEditPlanModal(false);
            fetchAdminUsers();
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.error || "Erro ao atualizar dados.", { id: loadToast });
        }
    };

    const fetchBroadcastGroups = async () => {
        setLoadingGroups(true);
        try {
            const res = await api.get('/bot/admin/broadcast/groups');
            setBroadcastGroups(res.data || []);
            setSelectedGroups([]);
        } catch (err) {
            console.error("Erro ao carregar grupos para divulgação", err);
            toast.error("Erro ao carregar grupos de divulgação.");
        } finally {
            setLoadingGroups(false);
        }
    };

    const fetchBroadcastStatus = async () => {
        try {
            const res = await api.get('/bot/admin/broadcast/status');
            setBroadcastStatus(res.data);
            if (res.data?.running) {
                setSendingBroadcast(true);
            } else {
                setSendingBroadcast(false);
            }
        } catch (err) {
            console.error("Erro ao carregar status da transmissão", err);
        }
    };

    const fetchBroadcastLogs = async () => {
        setLoadingLogs(true);
        try {
            const res = await api.get('/bot/admin/broadcast/logs');
            setBroadcastLogs(res.data || []);
        } catch (err) {
            console.error("Erro ao carregar logs da transmissão", err);
            toast.error("Erro ao carregar histórico de divulgação.");
        } finally {
            setLoadingLogs(false);
        }
    };

    // User broadcast handlers
    const fetchUserBroadcastStatus = async () => {
        try {
            const res = await api.get('/bot/admin/broadcast-users/status');
            setUserBroadcastStatus(res.data);
            if (res.data?.running) {
                setSendingUserBroadcast(true);
            } else {
                setSendingUserBroadcast(false);
            }
        } catch (err) {
            console.error("Erro ao verificar status do disparo para usuários:", err);
        }
    };

    const fetchUserBroadcastLogs = async () => {
        setLoadingUserLogs(true);
        try {
            const res = await api.get('/bot/admin/broadcast-users/logs');
            setUserBroadcastLogs(res.data || []);
        } catch (err) {
            console.error("Erro ao carregar logs do disparo de usuários:", err);
        } finally {
            setLoadingUserLogs(false);
        }
    };

    const handleStopUserBroadcast = async () => {
        try {
            await api.post('/bot/admin/broadcast-users/stop');
            toast.success("Comando de parada enviado ao disparador.");
            fetchUserBroadcastStatus();
        } catch (err) {
            toast.error("Erro ao interromper disparo.");
        }
    };

    const handleSendTestUserMessage = async () => {
        if (!userTestNumber.trim()) {
            return toast.error("Digite o número de WhatsApp para teste com DDD.");
        }
        if (!userBroadcastMessage.trim()) {
            return toast.error("A mensagem não pode estar vazia.");
        }
        setTestingUserMessage(true);
        const loadToast = toast.loading("Enviando mensagem de teste no WhatsApp...");
        try {
            const res = await api.post('/bot/admin/broadcast-users/test', {
                testNumber: userTestNumber,
                message: userBroadcastMessage,
                instance: 'B'
            });
            toast.success(res.data?.message || "Teste enviado com sucesso!", { id: loadToast });
        } catch (err) {
            toast.error(err.response?.data?.error || "Erro ao enviar mensagem de teste.", { id: loadToast });
        } finally {
            setTestingUserMessage(false);
        }
    };

    const handleStartUserBroadcast = async (e) => {
        e.preventDefault();
        if (!userBroadcastMessage.trim()) {
            return toast.error("A mensagem não pode estar vazia.");
        }
        if (selectedUserIds.length === 0) {
            return toast.error("Selecione ao menos um afiliado com WhatsApp na lista abaixo para disparar.");
        }
        setSendingUserBroadcast(true);
        const loadToast = toast.loading(`Iniciando campanha para ${selectedUserIds.length} afiliado(s)...`);
        try {
            const res = await api.post('/bot/admin/broadcast-users/send', {
                message: userBroadcastMessage,
                instance: 'B',
                minDelay: userMinDelay,
                maxDelay: userMaxDelay,
                selectedUserIds: selectedUserIds
            });
            toast.success(res.data?.message || `Disparo para ${selectedUserIds.length} usuários iniciado!`, { id: loadToast });
            fetchUserBroadcastStatus();
            fetchUserBroadcastLogs();
        } catch (err) {
            toast.error(err.response?.data?.error || "Erro ao iniciar disparo para usuários.", { id: loadToast });
            setSendingUserBroadcast(false);
        }
    };

    // Helper functions para seleção de afiliados no Disparo Avulso
    const filteredUsersForBroadcast = adminUsers.filter(u => {
        if (userFilterPlan !== 'all') {
            if (userFilterPlan === 'free' && !['free', 'free_divulgador'].includes(u.plan)) return false;
            if (userFilterPlan === 'basic' && !['basic', 'basic_divulgador'].includes(u.plan)) return false;
            if (userFilterPlan === 'premium' && !['premium', 'premium_divulgador'].includes(u.plan)) return false;
            if (userFilterPlan === 'divulgador' && !String(u.plan).endsWith('_divulgador')) return false;
        }

        if (userFilterAudience === 'verified_only' && !u.userWhatsappVerified) return false;
        if (userFilterAudience === 'recent_7_days') {
            const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
            if (new Date(u.createdAt) < sevenDaysAgo) return false;
        }

        if (userPhoneFilter === 'with_phone' && !u.userWhatsappNumber) return false;
        if (userPhoneFilter === 'without_phone' && u.userWhatsappNumber) return false;

        if (userSearchTerm && userSearchTerm.trim()) {
            const term = userSearchTerm.toLowerCase().trim();
            const matchName = u.name && u.name.toLowerCase().includes(term);
            const matchUsername = u.username && u.username.toLowerCase().includes(term);
            const matchEmail = u.email && u.email.toLowerCase().includes(term);
            const matchPhone = u.userWhatsappNumber && String(u.userWhatsappNumber).includes(term);
            if (!matchName && !matchUsername && !matchEmail && !matchPhone) return false;
        }

        return true;
    });

    const usersWithPhoneInFilter = filteredUsersForBroadcast.filter(u => !!u.userWhatsappNumber);
    const isAllSelected = usersWithPhoneInFilter.length > 0 && usersWithPhoneInFilter.every(u => selectedUserIds.includes(u.id));

    const handleToggleSelectAll = () => {
        if (isAllSelected) {
            const filterIds = new Set(usersWithPhoneInFilter.map(u => u.id));
            setSelectedUserIds(prev => prev.filter(id => !filterIds.has(id)));
        } else {
            const newIds = new Set([...selectedUserIds, ...usersWithPhoneInFilter.map(u => u.id)]);
            setSelectedUserIds(Array.from(newIds));
        }
    };

    const handleToggleUserSelect = (id) => {
        setSelectedUserIds(prev => 
            prev.includes(id) ? prev.filter(userId => userId !== id) : [...prev, id]
        );
    };

    const handleClearSelection = () => {
        setSelectedUserIds([]);
    };

    // Helper functions para Gerenciamento de Instâncias WhatsApp (A e B)
    const handleLogoutAdmin = async (instance) => {
        const ok = await confirm({
            title: `Desconectar Instância ${instance}`,
            message: `Deseja realmente desconectar a Instância ${instance}? As automações e disparos através deste número serão pausados até nova leitura do QR Code.`,
            confirmText: "Sim, desconectar",
            cancelText: "Cancelar",
            type: "danger"
        });
        if (!ok) return;

        const loadingToast = toast.loading(`Desconectando Instância ${instance}...`);
        try {
            await api.get(`/config/whatsapp/logout/${instance}`);
            toast.success(`Instância ${instance} desconectada com sucesso!`, { id: loadingToast });
            if (instance === 'A') {
                setWhatsStatusA('Iniciando...');
                setQrCodeA('');
            } else {
                setWhatsStatusB('Iniciando...');
                setQrCodeB('');
            }
            setTimeout(() => {
                checkInstanceStatus(instance);
            }, 3000);
        } catch (err) {
            toast.error(err.response?.data?.error || "Erro ao desconectar instância", { id: loadingToast });
        }
    };

    const handleRestartWhatsapp = async (instance) => {
        const loadingToast = toast.loading(`Reiniciando motor da Instância ${instance}...`);
        try {
            await api.get(`/config/whatsapp/restart/${instance}`);
            toast.success(`Motor da Instância ${instance} reiniciado!`, { id: loadingToast });
            if (instance === 'A') {
                setWhatsStatusA('Iniciando...');
                setQrCodeA('');
            } else {
                setWhatsStatusB('Iniciando...');
                setQrCodeB('');
            }
            setTimeout(() => {
                checkInstanceStatus(instance);
            }, 3000);
        } catch (err) {
            toast.error(err.response?.data?.error || "Erro ao reiniciar motor", { id: loadingToast });
        }
    };

    const handleToggleMarketingActive = async () => {
        const nextVal = !whatsappMarketingActive;
        setWhatsappMarketingActive(nextVal);
        try {
            await api.post('/config', { whatsappMarketingActive: nextVal });
            toast.success(nextVal ? "Instância A (Marketing) ativada!" : "Instância A (Marketing) desativada!");
        } catch (e) {
            setWhatsappMarketingActive(!nextVal);
            toast.error("Erro ao alterar status da Instância A.");
        }
    };

    const handleSaveInstanceSettings = async (instance) => {
        setSavingInstanceConfig(true);
        const loadToast = toast.loading(`Salvando configurações da Instância ${instance}...`);
        try {
            const payload = instance === 'A' ? {
                whatsappAutoReplyA,
                aiPromptA,
                postarNoStatus
            } : {
                whatsappAutoReplyB,
                aiPromptB
            };
            await api.post('/config', payload);
            toast.success(`Configurações da Instância ${instance} salvas com sucesso!`, { id: loadToast });
        } catch (e) {
            toast.error("Erro ao salvar configurações da instância.", { id: loadToast });
        } finally {
            setSavingInstanceConfig(false);
        }
    };

    const loadExamplePrompt = (instance) => {
        const exampleAdmin = `Você é a Mayara, uma assistente de compras ultra-eficiente.
Sua missão principal é converter imagens e pedidos em vendas.

REGRAS DE OURO (Siga com rigor):
1. PRIORIDADE VISUAL: Se houver uma imagem, ANALISE-A IMEDIATAMENTE. Nunca peça foto se uma imagem já existe no contexto.
2. COMANDO DE BUSCA: Responda obrigatoriamente começando com "BUSCA: [Nome Curto]".
- Use no máximo 4 palavras.
- Remova tamanhos (ex: 39), conjunções (ex: ou, para) e detalhes inúteis.
3. FILTRO DE ACESSÓRIOS: Se identificar um objeto, foque no objeto principal, não em peças de reposição ou capas.
4. ZERO PAPO FURADO: Se houver imagem, ignore o texto do usuário e responda apenas o comando BUSCA.
5. PROIBIÇÃO DE COLCHETES: Nunca use "[especificar modelo]". Escolha o modelo mais provável pela imagem.
6. MODO CONVERSA: Apenas se NÃO houver imagem, seja breve, gentil e tente vender.
7. Se o usuário encaminhar algum link de um produto ou perguntar sobre você, explique de forma amigável que você compartilha ofertas encontradas na internet e que para comprar basta adicionar o produto ao carrinho na Shopee/Mercado Livre.`;

        if (instance === 'A') {
            setAiPromptA(exampleAdmin);
        } else {
            setAiPromptB(exampleAdmin);
        }
        toast.success(`Modelo de prompt para Instância ${instance} carregado! Não se esqueça de salvar.`);
    };

    // Handlers do Disparo de E-mail Marketing (Resend)
    const fetchEmailBroadcastStatus = async () => {
        try {
            const res = await api.get('/bot/admin/broadcast-email/status');
            setEmailBroadcastStatus(res.data);
            if (res.data?.status === 'running') {
                setSendingEmailBroadcast(true);
            } else {
                setSendingEmailBroadcast(false);
            }
        } catch (err) {
            console.error("Erro ao verificar status do envio de e-mails:", err);
        }
    };

    const fetchEmailBroadcastLogs = async () => {
        setLoadingEmailLogs(true);
        try {
            const res = await api.get('/bot/admin/broadcast-email/logs');
            setEmailBroadcastLogs(res.data || []);
        } catch (err) {
            console.error("Erro ao carregar histórico de e-mails:", err);
        } finally {
            setLoadingEmailLogs(false);
        }
    };

    const handleStopEmailBroadcast = async () => {
        try {
            await api.post('/bot/admin/broadcast-email/stop');
            toast.success("Solicitação de parada enviada ao disparador de e-mails.");
            fetchEmailBroadcastStatus();
        } catch (err) {
            toast.error("Erro ao interromper envio de e-mails.");
        }
    };

    const handleSendTestEmail = async () => {
        if (!emailBroadcastTestEmail.trim()) {
            return toast.error("Digite o e-mail de destino para o teste.");
        }
        if (!emailBroadcastSubject.trim()) {
            return toast.error("O assunto do e-mail é obrigatório.");
        }
        if (!emailBroadcastMessage.trim()) {
            return toast.error("A mensagem do e-mail não pode estar vazia.");
        }
        setTestingEmailMessage(true);
        const loadToast = toast.loading("Enviando e-mail de teste via Resend...");
        try {
            const res = await api.post('/bot/admin/broadcast-email/test', {
                testEmail: emailBroadcastTestEmail.trim(),
                subject: emailBroadcastSubject.trim(),
                message: emailBroadcastMessage.trim()
            });
            toast.success(res.data?.message || "E-mail de teste enviado com sucesso!", { id: loadToast });
        } catch (err) {
            toast.error(err.response?.data?.error || "Erro ao enviar e-mail de teste.", { id: loadToast });
        } finally {
            setTestingEmailMessage(false);
        }
    };

    const handleStartEmailBroadcast = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (!emailBroadcastSubject.trim()) {
            return toast.error("O assunto do e-mail é obrigatório.");
        }
        if (!emailBroadcastMessage.trim()) {
            return toast.error("A mensagem do e-mail não pode estar vazia.");
        }
        const ok = await confirm({
            title: "Confirmar Disparo de E-mails",
            message: "Deseja realmente iniciar a transmissão de e-mails para o público selecionado? O envio respeitará o limite de taxa do Resend.",
            confirmText: "Sim, Iniciar Disparo",
            cancelText: "Cancelar",
            type: "warning"
        });
        if (!ok) return;

        setSendingEmailBroadcast(true);
        const loadToast = toast.loading("Iniciando campanha de e-mails...");
        try {
            const res = await api.post('/bot/admin/broadcast-email/send', {
                filter: emailBroadcastFilter,
                subject: emailBroadcastSubject.trim(),
                message: emailBroadcastMessage.trim()
            });
            toast.success(res.data?.message || "Disparo de e-mails iniciado com sucesso!", { id: loadToast });
            fetchEmailBroadcastStatus();
            fetchEmailBroadcastLogs();
        } catch (err) {
            toast.error(err.response?.data?.error || "Erro ao iniciar disparo de e-mails.", { id: loadToast });
            setSendingEmailBroadcast(false);
        }
    };

    // Funções da Campanha Semanal (Ativação & Upgrade)
    const fetchWeeklyStats = async (isSilent = false) => {
        if (!isSilent) setLoadingWeeklyStats(true);
        try {
            const res = await api.get('/bot/admin/weekly-campaign/stats');
            setWeeklyStats(res.data);
            if (!isSilent && res.data?.templates) {
                if (res.data.templates.msgUpgrade) setWeeklyMsgUpgrade(res.data.templates.msgUpgrade);
                if (res.data.templates.msgActivation) setWeeklyMsgActivation(res.data.templates.msgActivation);
            }
        } catch (err) {
            console.error("Erro ao carregar campanha semanal:", err);
            if (!isSilent) toast.error("Erro ao carregar estatísticas da campanha semanal.");
        } finally {
            if (!isSilent) setLoadingWeeklyStats(false);
        }
    };

    const handleSaveWeeklyConfig = async () => {
        setSavingWeeklyConfig(true);
        const loadToast = toast.loading("Salvando configurações da campanha semanal...");
        try {
            await api.post('/bot/admin/weekly-campaign/config', {
                msgUpgrade: weeklyMsgUpgrade,
                msgActivation: weeklyMsgActivation,
                active: weeklyStats?.templates?.active,
                dayOfWeek: weeklyStats?.templates?.dayOfWeek,
                hour: weeklyStats?.templates?.hour
            });
            toast.success("Mensagens da campanha semanal salvas com sucesso!", { id: loadToast });
            fetchWeeklyStats(true);
        } catch (err) {
            toast.error(err.response?.data?.error || "Erro ao salvar mensagens.", { id: loadToast });
        } finally {
            setSavingWeeklyConfig(false);
        }
    };

    const handleToggleWeeklyActive = async () => {
        const novoStatus = !weeklyStats?.templates?.active;
        try {
            await api.post('/bot/admin/weekly-campaign/config', {
                active: novoStatus
            });
            toast.success(novoStatus ? "Automação semanal ATIVADA!" : "Automação semanal DESATIVADA.");
            fetchWeeklyStats(true);
        } catch (err) {
            toast.error("Erro ao alterar automação semanal.");
        }
    };

    const handleSendWeeklyTest = async () => {
        if (!weeklyTestNumber.trim()) {
            return toast.error("Informe seu número de WhatsApp com DDD para testar.");
        }
        setSendingWeeklyTest(true);
        const loadToast = toast.loading(`Enviando teste de ${weeklyTab === 'upgrade' ? 'Upgrade' : 'Ativação'}...`);
        try {
            await api.post('/bot/admin/weekly-campaign/test', {
                testNumber: weeklyTestNumber.trim(),
                type: weeklyTab,
                customMessage: weeklyTab === 'upgrade' ? weeklyMsgUpgrade : weeklyMsgActivation
            });
            toast.success("Mensagem de teste enviada! Confira no seu WhatsApp.", { id: loadToast });
        } catch (err) {
            toast.error(err.response?.data?.error || "Erro ao enviar teste.", { id: loadToast });
        } finally {
            setSendingWeeklyTest(false);
        }
    };

    const handleStartWeeklyCampaign = async () => {
        const total = (weeklyStats?.counts?.eligibleUpgrade || 0) + (weeklyStats?.counts?.eligibleActivation || 0);
        if (total === 0) {
            return toast.error("Nenhum usuário elegível para a campanha semanal nesta semana.");
        }

        const ok = await confirm({
            title: "Iniciar Campanha Semanal",
            message: `Deseja iniciar o envio para ${total} usuários Free elegíveis?\n\n• ${weeklyStats?.counts?.eligibleUpgrade || 0} receberão a Mensagem de Upgrade (Com API)\n• ${weeklyStats?.counts?.eligibleActivation || 0} receberão a Mensagem de Ativação (Sem API)\n\nIntervalo anti-ban: ${weeklyMinDelay} a ${weeklyMaxDelay} segundos.`,
            confirmText: "Iniciar Agora",
            cancelText: "Cancelar"
        });

        if (!ok) return;

        setSendingWeeklyCampaign(true);
        const loadToast = toast.loading("Iniciando campanha semanal...");
        try {
            await api.post('/bot/admin/weekly-campaign/send', {
                minDelay: weeklyMinDelay,
                maxDelay: weeklyMaxDelay,
                msgUpgradeCustom: weeklyMsgUpgrade,
                msgActivationCustom: weeklyMsgActivation
            });
            toast.success("Campanha semanal iniciada com sucesso em segundo plano!", { id: loadToast });
            fetchWeeklyStats(true);
        } catch (err) {
            toast.error(err.response?.data?.error || "Erro ao iniciar campanha.", { id: loadToast });
        } finally {
            setSendingWeeklyCampaign(false);
        }
    };

    const handleStopWeeklyCampaign = async () => {
        try {
            await api.post('/bot/admin/weekly-campaign/stop');
            toast.success("Campanha semanal interrompida!");
            fetchWeeklyStats(true);
        } catch (err) {
            toast.error("Erro ao interromper.");
        }
    };

    // Poll status when active tab is broadcast
    useEffect(() => {
        let timer;
        if (activeTab === 'broadcast') {
            if (broadcastSubTab === 'weekly') {
                fetchWeeklyStats();
                timer = setInterval(() => {
                    fetchWeeklyStats(true);
                }, 3000);
            } else if (broadcastSubTab === 'groups') {
                fetchBroadcastGroups();
                fetchBroadcastLogs();
                fetchBroadcastStatus();

                timer = setInterval(() => {
                    fetchBroadcastStatus();
                }, 3000);
            } else if (broadcastSubTab === 'email') {
                fetchEmailBroadcastLogs();
                fetchEmailBroadcastStatus();

                timer = setInterval(() => {
                    fetchEmailBroadcastStatus();
                }, 3000);
            } else {
                fetchUserBroadcastLogs();
                fetchUserBroadcastStatus();

                timer = setInterval(() => {
                    fetchUserBroadcastStatus();
                }, 3000);
            }
        }
        return () => {
            if (timer) clearInterval(timer);
        };
    }, [activeTab, broadcastSubTab]);

    const handleSendBroadcast = (e) => {
        e.preventDefault();
        if (!broadcastMessage.trim()) {
            toast.error("Por favor, digite uma mensagem para enviar.");
            return;
        }
        if (selectedGroups.length === 0) {
            toast.error("Selecione pelo menos um grupo de destino.");
            return;
        }
        setShowConfirmModal(true);
    };

    const executeBroadcast = async () => {
        setShowConfirmModal(false);
        setSendingBroadcast(true);
        const loadToast = toast.loading("Iniciando transmissão de divulgação...");
        try {
            const payload = {
                message: broadcastMessage,
                instance: broadcastInstance,
                groupIds: selectedGroups
            };
            const res = await api.post('/bot/admin/broadcast/send', payload);
            toast.success(res.data?.message || "Transmissão iniciada!", { id: loadToast });
            setBroadcastMessage(DEFAULT_BROADCAST_TEMPLATE);
            fetchBroadcastStatus();
            fetchBroadcastLogs();
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.error || "Erro ao iniciar transmissão.", { id: loadToast });
            setSendingBroadcast(false);
        }
    };

    const handleToggleSelectGroup = (groupId) => {
        setSelectedGroups(prev => {
            const current = [...prev];
            const index = current.indexOf(groupId);
            if (index > -1) {
                current.splice(index, 1);
            } else {
                current.push(groupId);
            }
            return current;
        });
    };

    const handleSelectAllFiltered = (filteredIds) => {
        setSelectedGroups(prev => {
            const newSelection = [...prev];
            filteredIds.forEach(id => {
                if (!newSelection.includes(id)) {
                    newSelection.push(id);
                }
            });
            return newSelection;
        });
    };

    const handleDeselectAllFiltered = (filteredIds) => {
        setSelectedGroups(prev => {
            return prev.filter(id => !filteredIds.includes(id));
        });
    };

    const filteredGroups = broadcastGroups.filter(g =>
        g.nome.toLowerCase().includes(searchGroupQuery.toLowerCase()) ||
        g.whatsappId.includes(searchGroupQuery)
    );

    useEffect(() => {
        // Bloqueio extra no frontend
        if (userPlan !== 'admin' && userPlan !== 'moderator' && userPlan !== 'support') {
            window.location.href = '/admin';
            return;
        }

        const fetchConfig = async () => {
            try {
                const res = await api.get('/config');
                setConfig(prev => ({
                    ...prev,
                    limiteGruposFree: res.data.limiteGruposFree ?? 1,
                    limiteGruposBasic: res.data.limiteGruposBasic ?? 5,
                    cooldownTrocaGrupoSegundos: res.data.cooldownTrocaGrupoSegundos ?? 3600,
                    cooldownDisparoFreeSegundos: res.data.cooldownDisparoFreeSegundos ?? 86400,
                    priceBasic: res.data.priceBasic ?? 49.90,
                    pricePremium: res.data.pricePremium ?? 99.90,
                    priceDivulgadorBasic: res.data.priceDivulgadorBasic ?? 39.90,
                    priceDivulgadorPremium: res.data.priceDivulgadorPremium ?? 79.90,
                    landingWhatsappNumber: res.data.landingWhatsappNumber ?? '',
                    defaultTrialDays: res.data.defaultTrialDays ?? 7,
                    emailServiceActive: res.data.emailServiceActive !== undefined ? res.data.emailServiceActive : true,
                    whatsappSystemActive: res.data.whatsappSystemActive !== undefined ? res.data.whatsappSystemActive : true,
                    emailWelcomeActive: res.data.emailWelcomeActive !== undefined ? res.data.emailWelcomeActive : true
                }));
            } catch (err) {
                console.error("Erro ao carregar configs de admin", err);
                toast.error("Erro ao carregar configurações do sistema.");
            }
        };
        fetchConfig();
    }, [userPlan]);

    // Load coupons when tab changes to 'coupons'
    useEffect(() => {
        if (activeTab === 'coupons') {
            fetchCoupons();
        }
    }, [activeTab]);

    // Load users when tab changes to 'users' or 'broadcast' (subtab 'users')
    useEffect(() => {
        if (activeTab === 'users' || (activeTab === 'broadcast' && broadcastSubTab === 'users')) {
            fetchAdminUsers();
        }
    }, [activeTab, broadcastSubTab]);

    const checkInstanceStatus = async (instance) => {
        try {
            const res = await api.get(`/config/whatsapp/status/${instance}`);
            const setStatus = instance === 'A' ? setWhatsStatusA : setWhatsStatusB;
            const setQr = instance === 'A' ? setQrCodeA : setQrCodeB;

            if (res.data.connected) {
                setStatus('Conectado');
                setQr('');
            } else if (res.data.state === 'INITIALIZING') {
                setStatus('Iniciando...');
                setQr('');
            } else {
                if (res.data.qrCode) {
                    setQr(res.data.qrCode);
                    setStatus('Aguardando leitura');
                } else {
                    setStatus('Desconectado');
                    setQr('');
                }
            }
        } catch (err) {
            if (instance === 'A') setWhatsStatusA('Desconectado');
            else setWhatsStatusB('Desconectado');
        }
    };

    useEffect(() => {
        checkInstanceStatus('A');
        checkInstanceStatus('B');

        const interval = setInterval(() => {
            checkInstanceStatus('A');
            checkInstanceStatus('B');
        }, 5000);

        socket.on('qrCode_A', (qr) => { setQrCodeA(qr); setWhatsStatusA('Aguardando leitura'); });
        socket.on('auth_success_A', (isAuth) => { setWhatsStatusA(isAuth ? 'Conectado' : 'Desconectado'); if (isAuth) setQrCodeA(''); });
        
        socket.on('qrCode_B', (qr) => { setQrCodeB(qr); setWhatsStatusB('Aguardando leitura'); });
        socket.on('auth_success_B', (isAuth) => { setWhatsStatusB(isAuth ? 'Conectado' : 'Desconectado'); if (isAuth) setQrCodeB(''); });

        api.get('/config').then(res => {
            if (res.data) {
                setWhatsappMarketingActive(res.data.whatsappMarketingActive !== false);
                setWhatsappAutoReplyA(Boolean(res.data.whatsappAutoReplyA));
                setAiPromptA(res.data.aiPromptA || '');
                setWhatsappAutoReplyB(Boolean(res.data.whatsappAutoReplyB));
                setAiPromptB(res.data.aiPromptB || '');
                setPostarNoStatus(Boolean(res.data.postarNoStatus));
            }
        }).catch(e => console.error("Erro ao carregar configurações das instâncias:", e));

        return () => {
            clearInterval(interval);
            socket.off('qrCode_A');
            socket.off('auth_success_A');
            socket.off('qrCode_B');
            socket.off('auth_success_B');
        };
    }, []);

    const fetchCoupons = async () => {
        setLoadingCoupons(true);
        try {
            const res = await api.get('/payment/coupons');
            setCoupons(res.data || []);
        } catch (err) {
            console.error("Erro ao carregar cupons", err);
            toast.error("Erro ao carregar cupons.");
        } finally {
            setLoadingCoupons(false);
        }
    };

    const handleSave = async () => {
        setIsLoading(true);
        const loadingToast = toast.loading("Salvando configurações globais...");

        try {
            const payload = {
                limiteGruposFree: parseInt(config.limiteGruposFree, 10),
                limiteGruposBasic: parseInt(config.limiteGruposBasic, 10),
                cooldownTrocaGrupoSegundos: parseInt(config.cooldownTrocaGrupoSegundos, 10),
                cooldownDisparoFreeSegundos: parseInt(config.cooldownDisparoFreeSegundos, 10),
                priceBasic: parseFloat(config.priceBasic) || 49.90,
                pricePremium: parseFloat(config.pricePremium) || 99.90,
                priceDivulgadorBasic: parseFloat(config.priceDivulgadorBasic) || 39.90,
                priceDivulgadorPremium: parseFloat(config.priceDivulgadorPremium) || 79.90,
                landingWhatsappNumber: config.landingWhatsappNumber || '',
                defaultTrialDays: parseInt(config.defaultTrialDays, 10) || 7,
                emailServiceActive: config.emailServiceActive !== undefined ? !!config.emailServiceActive : true,
                whatsappSystemActive: config.whatsappSystemActive !== undefined ? !!config.whatsappSystemActive : true,
                emailWelcomeActive: config.emailWelcomeActive !== undefined ? !!config.emailWelcomeActive : true
            };

            await api.post('/config', payload);
            toast.success("Regras do sistema updated com sucesso!", { id: loadingToast });
        } catch (err) {
            toast.error(err.response?.data?.error || "Erro ao salvar", { id: loadingToast });
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateCoupon = async (e) => {
        e.preventDefault();
        if (!newCoupon.code.trim()) {
            toast.error("O código do cupom é obrigatório.");
            return;
        }
        if (!newCoupon.discountPercentage || newCoupon.discountPercentage <= 0 || newCoupon.discountPercentage > 100) {
            toast.error("Porcentagem de desconto deve ser entre 1 e 100.");
            return;
        }

        setIsLoading(true);
        const loadToast = toast.loading("Criando cupom...");
        try {
            const payload = {
                code: newCoupon.code.toUpperCase().trim(),
                discountPercentage: parseInt(newCoupon.discountPercentage, 10),
                onlyFirstSubscription: newCoupon.onlyFirstSubscription,
                allowedPlans: newCoupon.allowedPlans.length > 0 ? newCoupon.allowedPlans.join(',') : null,
                expirationDate: newCoupon.expirationDate ? newCoupon.expirationDate : null,
                maxUses: newCoupon.maxUses ? parseInt(newCoupon.maxUses, 10) : null,
                duration: newCoupon.duration
            };

            await api.post('/payment/coupons', payload);
            toast.success("Cupom criado com sucesso!", { id: loadToast });

            // Reset form
            setNewCoupon({
                code: '',
                discountPercentage: '',
                onlyFirstSubscription: false,
                allowedPlans: [],
                expirationDate: '',
                maxUses: '',
                duration: 'once'
            });
            fetchCoupons();
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.error || "Erro ao criar cupom.", { id: loadToast });
        } finally {
            setIsLoading(false);
        }
    };

    const handleToggleCoupon = async (id) => {
        try {
            await api.patch(`/payment/coupons/${id}/toggle`);
            toast.success("Status do cupom atualizado!");
            fetchCoupons();
        } catch (err) {
            console.error(err);
            toast.error("Erro ao alterar status do cupom.");
        }
    };

    const handleDeleteCoupon = async (id) => {
        const confirmed = await confirm({
            title: "Excluir Cupom",
            message: "Deseja realmente excluir este cupom de desconto? Esta ação também o desativará permanentemente no Stripe.",
            confirmText: "Excluir Cupom",
            cancelText: "Cancelar",
            type: "danger"
        });
        if (!confirmed) return;

        const loadToast = toast.loading("Excluindo cupom...");
        try {
            await api.delete(`/payment/coupons/${id}`);
            toast.success("Cupom deletado com sucesso!", { id: loadToast });
            fetchCoupons();
        } catch (err) {
            console.error(err);
            toast.error("Erro ao deletar cupom.", { id: loadToast });
        }
    };

    const handleAllowedPlanChange = (plan) => {
        setNewCoupon(prev => {
            const allowed = [...prev.allowedPlans];
            const index = allowed.indexOf(plan);
            if (index > -1) {
                allowed.splice(index, 1);
            } else {
                allowed.push(plan);
            }
            return { ...prev, allowedPlans: allowed };
        });
    };

    if (userPlan !== 'admin' && userPlan !== 'moderator' && userPlan !== 'support') return null;

    return (
        <div className="page-wrapper" style={{ paddingBottom: '50px', fontFamily: 'Outfit, sans-serif' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '30px' }}>
                <div style={{ backgroundColor: '#f3e8ff', padding: '15px', borderRadius: '15px' }}>
                    <Shield size={32} color="#9333ea" />
                </div>
                <div>
                    <h1 style={{ margin: 0, color: '#1e293b' }}>Painel do Administrador</h1>
                    <p style={{ margin: 0, color: '#64748b', fontSize: '14px', marginTop: '5px' }}>
                        Controle global de planos, limites de grupos, regras do sistema e cupons de desconto.
                    </p>
                </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginBottom: '30px', borderBottom: '1px solid #e2e8f0', paddingBottom: '12px', flexWrap: 'wrap' }}>
                <button
                    onClick={() => setActiveTab('rules')}
                    style={{
                        padding: '10px 20px',
                        borderRadius: '8px',
                        border: 'none',
                        backgroundColor: activeTab === 'rules' ? '#9333ea' : 'transparent',
                        color: activeTab === 'rules' ? 'white' : '#64748b',
                        fontWeight: '600',
                        fontSize: '14px',
                        cursor: 'pointer',
                        transition: '0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                >
                    <Save size={18} /> Configurações Gerais
                </button>
                {userPlan === 'admin' && (
                    <button
                        onClick={() => setActiveTab('coupons')}
                        style={{
                            padding: '10px 20px',
                            borderRadius: '8px',
                            border: 'none',
                            backgroundColor: activeTab === 'coupons' ? '#9333ea' : 'transparent',
                            color: activeTab === 'coupons' ? 'white' : '#64748b',
                            fontWeight: '600',
                            fontSize: '14px',
                            cursor: 'pointer',
                            transition: '0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        <Ticket size={18} /> Cupons de Desconto
                    </button>
                )}
                <button
                    onClick={() => setActiveTab('broadcast')}
                    style={{
                        padding: '10px 20px',
                        borderRadius: '8px',
                        border: 'none',
                        backgroundColor: activeTab === 'broadcast' ? '#9333ea' : 'transparent',
                        color: activeTab === 'broadcast' ? 'white' : '#64748b',
                        fontWeight: '600',
                        fontSize: '14px',
                        cursor: 'pointer',
                        transition: '0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                >
                    <Megaphone size={18} /> Divulgação
                </button>
                <button
                    onClick={() => setActiveTab('whatsapp_instances')}
                    style={{
                        padding: '10px 20px',
                        borderRadius: '8px',
                        border: 'none',
                        backgroundColor: activeTab === 'whatsapp_instances' ? '#9333ea' : 'transparent',
                        color: activeTab === 'whatsapp_instances' ? 'white' : '#64748b',
                        fontWeight: '600',
                        fontSize: '14px',
                        cursor: 'pointer',
                        transition: '0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                >
                    <MessageCircle size={18} /> Instâncias WhatsApp
                </button>
                <button
                    onClick={() => setActiveTab('users')}
                    style={{
                        padding: '10px 20px',
                        borderRadius: '8px',
                        border: 'none',
                        backgroundColor: activeTab === 'users' ? '#9333ea' : 'transparent',
                        color: activeTab === 'users' ? 'white' : '#64748b',
                        fontWeight: '600',
                        fontSize: '14px',
                        cursor: 'pointer',
                        transition: '0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                >
                    <Users size={18} /> Usuários
                </button>
            </div>

            {activeTab === 'rules' && (
                <div>
                    <div style={{
                        backgroundColor: '#fffbeb',
                        border: '1px solid #fef3c7',
                        padding: '15px 20px',
                        borderRadius: '12px',
                        marginBottom: '30px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                    }}>
                        <AlertTriangle size={20} color="#b45309" />
                        <span style={{ fontSize: '13px', color: '#92400e', fontWeight: '500' }}>
                            <strong>Atenção:</strong> As alterações feitas nesta página afetam imediatamente todos os usuários ativos do sistema e são aplicadas em tempo real.
                        </span>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '25px', marginBottom: '30px' }}>

                        <div className="config-card" style={{ background: 'white', padding: '25px', borderRadius: '20px', boxShadow: '0 4px 15px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                                <div style={{ backgroundColor: '#eff6ff', padding: '8px', borderRadius: '10px' }}>
                                    <Users size={20} color="#3b82f6" />
                                </div>
                                <h3 style={{ margin: 0, fontSize: '16px', color: '#1e293b' }}>Limites de Grupos por Plano</h3>
                            </div>

                            <div className="form-group" style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>Limite de Grupos Ativos (Plano Free)</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={config.limiteGruposFree}
                                    onChange={(e) => setConfig({ ...config, limiteGruposFree: e.target.value })}
                                    style={{ width: '100%', padding: '12px 15px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                                />
                            </div>

                            <div className="form-group">
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>Limite de Grupos Ativos (Plano Basic)</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={config.limiteGruposBasic}
                                    onChange={(e) => setConfig({ ...config, limiteGruposBasic: e.target.value })}
                                    style={{ width: '100%', padding: '12px 15px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                                />
                            </div>
                        </div>

                        <div className="config-card" style={{ background: 'white', padding: '25px', borderRadius: '20px', boxShadow: '0 4px 15px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                                <div style={{ backgroundColor: '#fef2f2', padding: '8px', borderRadius: '10px' }}>
                                    <Clock size={20} color="#ef4444" />
                                </div>
                                <h3 style={{ margin: 0, fontSize: '16px', color: '#1e293b' }}>Restrições de Tempo (Cooldown)</h3>
                            </div>

                            <div className="form-group" style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>Cooldown de Troca de Grupo (Segundos)</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={config.cooldownTrocaGrupoSegundos}
                                    onChange={(e) => setConfig({ ...config, cooldownTrocaGrupoSegundos: e.target.value })}
                                    style={{ width: '100%', padding: '12px 15px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                                />
                            </div>

                            <div className="form-group">
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>Cooldown de Disparos Plano Free (Segundos)</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={config.cooldownDisparoFreeSegundos}
                                    onChange={(e) => setConfig({ ...config, cooldownDisparoFreeSegundos: e.target.value })}
                                    style={{ width: '100%', padding: '12px 15px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                                />
                            </div>
                        </div>

                        <div className="config-card" style={{ background: 'white', padding: '25px', borderRadius: '20px', boxShadow: '0 4px 15px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                                <div style={{ backgroundColor: '#fdf2f8', padding: '8px', borderRadius: '10px' }}>
                                    <CreditCard size={20} color="#db2777" />
                                </div>
                                <h3 style={{ margin: 0, fontSize: '16px', color: '#1e293b' }}>Valores dos Planos (Stripe)</h3>
                            </div>

                            <div className="form-group" style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>Preço do Plano Básico (R$)</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={config.priceBasic}
                                    onChange={(e) => setConfig({ ...config, priceBasic: e.target.value })}
                                    style={{ width: '100%', padding: '12px 15px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                                />
                            </div>

                            <div className="form-group" style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>Preço do Plano Profissional (R$)</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={config.pricePremium}
                                    onChange={(e) => setConfig({ ...config, pricePremium: e.target.value })}
                                    style={{ width: '100%', padding: '12px 15px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                                />
                            </div>

                            <div className="form-group" style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>Preço do Plano Básico Divulgador (R$)</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={config.priceDivulgadorBasic}
                                    onChange={(e) => setConfig({ ...config, priceDivulgadorBasic: e.target.value })}
                                    style={{ width: '100%', padding: '12px 15px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                                />
                            </div>

                            <div className="form-group">
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>Preço do Plano Profissional Divulgador (R$)</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={config.priceDivulgadorPremium}
                                    onChange={(e) => setConfig({ ...config, priceDivulgadorPremium: e.target.value })}
                                    style={{ width: '100%', padding: '12px 15px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                                />
                            </div>
                        </div>

                        <div className="config-card" style={{ background: 'white', padding: '25px', borderRadius: '20px', boxShadow: '0 4px 15px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                                <div style={{ backgroundColor: '#e8f5e9', padding: '8px', borderRadius: '10px' }}>
                                    <MessageSquare size={20} color="#2e7d32" />
                                </div>
                                <h3 style={{ margin: 0, fontSize: '16px', color: '#1e293b' }}>Contato da Landing Page</h3>
                            </div>

                            <div className="form-group">
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>Número do WhatsApp (com DDI e DDD)</label>
                                <input
                                    type="text"
                                    placeholder="Ex: 5511999999999"
                                    value={config.landingWhatsappNumber}
                                    onChange={(e) => setConfig({ ...config, landingWhatsappNumber: e.target.value })}
                                    style={{ width: '100%', padding: '12px 15px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                                />
                                <span style={{ display: 'block', fontSize: '11px', color: '#64748b', marginTop: '5px' }}>
                                    Este número será utilizado no botão de WhatsApp na página pública (Landing Page).
                                </span>
                            </div>
                        </div>

                        <div className="config-card" style={{ background: 'white', padding: '25px', borderRadius: '20px', boxShadow: '0 4px 15px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                                <div style={{ backgroundColor: '#fef2f2', padding: '8px', borderRadius: '10px' }}>
                                    <Clock size={20} color="#ef4444" />
                                </div>
                                <h3 style={{ margin: 0, fontSize: '16px', color: '#1e293b' }}>Amostra Grátis</h3>
                            </div>

                            <div className="form-group">
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>Dias de Amostra Grátis Padrão</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={config.defaultTrialDays}
                                    onChange={(e) => setConfig({ ...config, defaultTrialDays: e.target.value })}
                                    style={{ width: '100%', padding: '12px 15px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                                />
                                <span style={{ display: 'block', fontSize: '11px', color: '#64748b', marginTop: '5px' }}>
                                    Quantidade de dias de acesso grátis concedidos aos usuários que resgatarem a amostra.
                                </span>
                            </div>
                        </div>

                        <div className="config-card" style={{ background: 'white', padding: '25px', borderRadius: '20px', boxShadow: '0 4px 15px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                                <div style={{ backgroundColor: '#f0fdf4', padding: '8px', borderRadius: '10px' }}>
                                    <Mail size={20} color="#16a34a" />
                                </div>
                                <div>
                                    <h3 style={{ margin: 0, fontSize: '16px', color: '#1e293b' }}>Canais de Comunicação do Sistema</h3>
                                    <span style={{ fontSize: '11px', color: '#64748b' }}>Ative ou pause disparos e canais de recuperação</span>
                                </div>
                            </div>

                            {/* Toggle 1: Email Resend */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', backgroundColor: '#f8fafc', borderRadius: '12px', marginBottom: '12px', border: '1px solid #e2e8f0' }}>
                                <div style={{ maxWidth: '80%' }}>
                                    <div style={{ fontWeight: '700', fontSize: '13px', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <Mail size={15} color="#0284c7" /> Envio de E-mails (Resend)
                                    </div>
                                    <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                                        Ativa todos os envios de e-mail (recuperação de senha, boas-vindas e campanhas). Desative se a cota diária expirar.
                                    </div>
                                </div>
                                <label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', flexShrink: 0 }}>
                                    <input
                                        type="checkbox"
                                        checked={config.emailServiceActive !== false}
                                        onChange={(e) => setConfig({ ...config, emailServiceActive: e.target.checked })}
                                        style={{ opacity: 0, width: 0, height: 0 }}
                                    />
                                    <span style={{
                                        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                        backgroundColor: config.emailServiceActive !== false ? '#10b981' : '#cbd5e1',
                                        borderRadius: '24px', transition: '.3s'
                                    }}>
                                        <span style={{
                                            position: 'absolute', height: '18px', width: '18px', left: config.emailServiceActive !== false ? '23px' : '3px', bottom: '3px',
                                            backgroundColor: 'white', borderRadius: '50%', transition: '.3s'
                                        }} />
                                    </span>
                                </label>
                            </div>

                            {/* Toggle 2: WhatsApp System (Instancia B) */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', backgroundColor: '#f8fafc', borderRadius: '12px', marginBottom: '12px', border: '1px solid #e2e8f0' }}>
                                <div style={{ maxWidth: '80%' }}>
                                    <div style={{ fontWeight: '700', fontSize: '13px', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <MessageSquare size={15} color="#16a34a" /> WhatsApp do Sistema (Instância B)
                                    </div>
                                    <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                                        Ativa envio de códigos de recuperação e verificação via WhatsApp. Desative se o número cair ou pausar.
                                    </div>
                                </div>
                                <label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', flexShrink: 0 }}>
                                    <input
                                        type="checkbox"
                                        checked={config.whatsappSystemActive !== false}
                                        onChange={(e) => setConfig({ ...config, whatsappSystemActive: e.target.checked })}
                                        style={{ opacity: 0, width: 0, height: 0 }}
                                    />
                                    <span style={{
                                        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                        backgroundColor: config.whatsappSystemActive !== false ? '#10b981' : '#cbd5e1',
                                        borderRadius: '24px', transition: '.3s'
                                    }}>
                                        <span style={{
                                            position: 'absolute', height: '18px', width: '18px', left: config.whatsappSystemActive !== false ? '23px' : '3px', bottom: '3px',
                                            backgroundColor: 'white', borderRadius: '50%', transition: '.3s'
                                        }} />
                                    </span>
                                </label>
                            </div>

                            {/* Toggle 3: Welcome Email on Unverified WhatsApp */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                <div style={{ maxWidth: '80%' }}>
                                    <div style={{ fontWeight: '700', fontSize: '13px', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <Sparkles size={15} color="#d97706" /> E-mail Automático de Boas-Vindas
                                    </div>
                                    <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                                        Envia e-mail de ativação e boas-vindas no cadastro para novas contas que ainda não confirmaram o WhatsApp.
                                    </div>
                                </div>
                                <label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer', flexShrink: 0 }}>
                                    <input
                                        type="checkbox"
                                        checked={config.emailWelcomeActive !== false}
                                        onChange={(e) => setConfig({ ...config, emailWelcomeActive: e.target.checked })}
                                        style={{ opacity: 0, width: 0, height: 0 }}
                                    />
                                    <span style={{
                                        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                        backgroundColor: config.emailWelcomeActive !== false ? '#10b981' : '#cbd5e1',
                                        borderRadius: '24px', transition: '.3s'
                                    }}>
                                        <span style={{
                                            position: 'absolute', height: '18px', width: '18px', left: config.emailWelcomeActive !== false ? '23px' : '3px', bottom: '3px',
                                            backgroundColor: 'white', borderRadius: '50%', transition: '.3s'
                                        }} />
                                    </span>
                                </label>
                            </div>
                        </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                        <button
                            onClick={handleSave}
                            disabled={isLoading}
                            style={{
                                backgroundColor: '#9333ea',
                                color: 'white',
                                border: 'none',
                                padding: '16px 30px',
                                borderRadius: '12px',
                                fontSize: '15px',
                                fontWeight: 'bold',
                                cursor: isLoading ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                boxShadow: '0 4px 15px rgba(147, 51, 234, 0.3)',
                                transition: '0.2s'
                            }}
                        >
                            <Save size={20} />
                            {isLoading ? "SALVANDO..." : "SALVAR REGRAS DO SISTEMA"}
                        </button>
                    </div>
                </div>
            )}

            {activeTab === 'coupons' && userPlan === 'admin' && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '30px', alignItems: 'start' }}>
                    <div style={{ background: 'white', padding: '25px', borderRadius: '20px', boxShadow: '0 4px 15px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                            <div style={{ backgroundColor: '#f5f3ff', padding: '8px', borderRadius: '10px' }}>
                                <Plus size={20} color="#9333ea" />
                            </div>
                            <h3 style={{ margin: 0, fontSize: '16px', color: '#1e293b' }}>Criar Novo Cupom</h3>
                        </div>

                        <form onSubmit={handleCreateCoupon} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            <div className="form-group">
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Código do Cupom</label>
                                <input
                                    type="text"
                                    placeholder="EX: BEMVINDO50"
                                    value={newCoupon.code}
                                    onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value.toUpperCase() })}
                                    style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none', textTransform: 'uppercase' }}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Porcentagem de Desconto (%)</label>
                                <input
                                    type="number"
                                    min="1"
                                    max="100"
                                    placeholder="EX: 15"
                                    value={newCoupon.discountPercentage}
                                    onChange={(e) => setNewCoupon({ ...newCoupon, discountPercentage: e.target.value })}
                                    style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none' }}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Duração do Desconto</label>
                                <select
                                    value={newCoupon.duration}
                                    onChange={(e) => setNewCoupon({ ...newCoupon, duration: e.target.value })}
                                    style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none', backgroundColor: 'white' }}
                                >
                                    <option value="once">Apenas no primeiro mês</option>
                                    <option value="forever">Para sempre (Recorrente)</option>
                                </select>
                            </div>

                            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 0' }}>
                                <input
                                    type="checkbox"
                                    id="onlyFirstSubscription"
                                    checked={newCoupon.onlyFirstSubscription}
                                    onChange={(e) => setNewCoupon({ ...newCoupon, onlyFirstSubscription: e.target.checked })}
                                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                />
                                <label htmlFor="onlyFirstSubscription" style={{ fontSize: '13px', fontWeight: '600', color: '#475569', cursor: 'pointer' }}>
                                    Apenas para 1ª assinatura na plataforma
                                </label>
                            </div>

                            <div className="form-group">
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Planos Permitidos</label>
                                <div style={{ display: 'flex', gap: '15px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={newCoupon.allowedPlans.includes('basic')}
                                            onChange={() => handleAllowedPlanChange('basic')}
                                            style={{ cursor: 'pointer' }}
                                        />
                                        Básico
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={newCoupon.allowedPlans.includes('premium')}
                                            onChange={() => handleAllowedPlanChange('premium')}
                                            style={{ cursor: 'pointer' }}
                                        />
                                        Profissional
                                    </label>
                                </div>
                            </div>

                            <div className="form-group">
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Limite Total de Usos (Opcional)</label>
                                <input
                                    type="number"
                                    min="1"
                                    placeholder="EX: 100"
                                    value={newCoupon.maxUses}
                                    onChange={(e) => setNewCoupon({ ...newCoupon, maxUses: e.target.value })}
                                    style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none' }}
                                />
                            </div>

                            <div className="form-group">
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Data Limite de Expiração (Opcional)</label>
                                <input
                                    type="date"
                                    value={newCoupon.expirationDate}
                                    onChange={(e) => setNewCoupon({ ...newCoupon, expirationDate: e.target.value })}
                                    style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none', backgroundColor: 'white' }}
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={isLoading}
                                style={{
                                    backgroundColor: '#9333ea',
                                    color: 'white',
                                    border: 'none',
                                    padding: '12px',
                                    borderRadius: '10px',
                                    fontSize: '14px',
                                    fontWeight: '700',
                                    cursor: isLoading ? 'not-allowed' : 'pointer',
                                    marginTop: '10px',
                                    transition: '0.2s',
                                    boxShadow: '0 4px 10px rgba(147, 51, 234, 0.2)'
                                }}
                            >
                                {isLoading ? "Criando..." : "CRIAR CUPOM"}
                            </button>
                        </form>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{ backgroundColor: '#f5f3ff', padding: '8px', borderRadius: '10px' }}>
                                    <Ticket size={20} color="#9333ea" />
                                </div>
                                <h3 style={{ margin: 0, fontSize: '16px', color: '#1e293b' }}>Cupons Cadastrados</h3>
                            </div>
                            <button
                                onClick={fetchCoupons}
                                disabled={loadingCoupons}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    color: '#9333ea',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                }}
                            >
                                <RefreshCw size={14} className={loadingCoupons ? "custom-spin" : ""} /> Atualizar
                            </button>
                        </div>

                        {loadingCoupons ? (
                            <div style={{ display: 'flex', justifyContent: 'center', padding: '45px' }}>
                                <RefreshCw className="custom-spin" size={32} style={{ color: '#9333ea' }} />
                            </div>
                        ) : coupons.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '50px 20px', background: 'white', borderRadius: '20px', border: '1px solid #e2e8f0', color: '#64748b' }}>
                                <Ticket size={40} style={{ color: '#cbd5e1', marginBottom: '15px' }} />
                                <p style={{ margin: 0, fontSize: '14px', fontWeight: '500' }}>Nenhum cupom cadastrado.</p>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                {coupons.map((coupon) => (
                                    <div
                                        key={coupon.id}
                                        style={{
                                            background: 'white',
                                            padding: '20px',
                                            borderRadius: '16px',
                                            border: '1px solid #e2e8f0',
                                            position: 'relative',
                                            opacity: coupon.active ? 1 : 0.7
                                        }}
                                    >
                                        <div style={{ position: 'absolute', top: '15px', right: '15px', display: 'flex', gap: '8px' }}>
                                            <button
                                                onClick={() => handleToggleCoupon(coupon.id)}
                                                style={{
                                                    background: coupon.active ? '#ecfdf5' : '#f1f5f9',
                                                    color: coupon.active ? '#10b981' : '#64748b',
                                                    border: 'none',
                                                    padding: '6px 10px',
                                                    borderRadius: '8px',
                                                    fontSize: '11px',
                                                    fontWeight: '700',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                {coupon.active ? 'Ativo' : 'Inativo'}
                                            </button>
                                            <button
                                                onClick={() => handleDeleteCoupon(coupon.id)}
                                                style={{
                                                    background: '#fef2f2',
                                                    color: '#ef4444',
                                                    border: 'none',
                                                    padding: '6px',
                                                    borderRadius: '8px',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>

                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                                            <span style={{ fontSize: '18px', fontWeight: '800', color: '#1e293b' }}>{coupon.code}</span>
                                            <span style={{ backgroundColor: '#f3e8ff', color: '#9333ea', fontSize: '12px', fontWeight: '800', padding: '3px 8px', borderRadius: '6px' }}>-{coupon.discountPercentage}%</span>
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#64748b', borderTop: '1px dashed #f1f5f9', paddingTop: '12px' }}>
                                            <div><strong>Duração:</strong> {coupon.duration === 'once' ? '1º mês' : 'Sempre'}</div>
                                            <div><strong>Usos:</strong> {coupon.usedCount}{coupon.maxUses ? ` / ${coupon.maxUses}` : ''}</div>
                                            {coupon.expirationDate && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '5px' }}>
                                                    <Calendar size={12} />
                                                    <span>Expira: {new Date(coupon.expirationDate).toLocaleDateString('pt-BR')}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
            {activeTab === 'broadcast' && (
                <div>
                    {/* Sub-abas de Transmissão */}
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', backgroundColor: '#e2e8f0', padding: '4px', borderRadius: '12px', width: 'fit-content' }}>
                        <button
                            type="button"
                            onClick={() => setBroadcastSubTab('weekly')}
                            style={{
                                padding: '8px 18px',
                                borderRadius: '8px',
                                border: 'none',
                                backgroundColor: broadcastSubTab === 'weekly' ? '#9333ea' : 'transparent',
                                color: broadcastSubTab === 'weekly' ? 'white' : '#475569',
                                fontWeight: '700',
                                fontSize: '13px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                transition: 'all 0.2s'
                            }}
                        >
                            <Sparkles size={16} /> Campanha Semanal
                            <span style={{
                                backgroundColor: broadcastSubTab === 'weekly' ? '#fae8ff' : '#9333ea',
                                color: broadcastSubTab === 'weekly' ? '#86198f' : 'white',
                                fontSize: '10px',
                                fontWeight: '800',
                                padding: '2px 6px',
                                borderRadius: '999px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.04em'
                            }}>
                                Automático
                            </span>
                        </button>
                        <button
                            type="button"
                            onClick={() => setBroadcastSubTab('users')}
                            style={{
                                padding: '8px 18px',
                                borderRadius: '8px',
                                border: 'none',
                                backgroundColor: broadcastSubTab === 'users' ? '#9333ea' : 'transparent',
                                color: broadcastSubTab === 'users' ? 'white' : '#475569',
                                fontWeight: '700',
                                fontSize: '13px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                transition: 'all 0.2s'
                            }}
                        >
                            <Users size={16} /> Disparo Avulso para Usuários
                        </button>
                        <button
                            type="button"
                            onClick={() => setBroadcastSubTab('groups')}
                            style={{
                                padding: '8px 18px',
                                borderRadius: '8px',
                                border: 'none',
                                backgroundColor: broadcastSubTab === 'groups' ? '#9333ea' : 'transparent',
                                color: broadcastSubTab === 'groups' ? 'white' : '#475569',
                                fontWeight: '700',
                                fontSize: '13px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                transition: 'all 0.2s'
                            }}
                        >
                            <Megaphone size={16} /> Disparar em Grupos de Ofertas
                        </button>
                        <button
                            type="button"
                            onClick={() => setBroadcastSubTab('email')}
                            style={{
                                padding: '8px 18px',
                                borderRadius: '8px',
                                border: 'none',
                                backgroundColor: broadcastSubTab === 'email' ? '#9333ea' : 'transparent',
                                color: broadcastSubTab === 'email' ? 'white' : '#475569',
                                fontWeight: '700',
                                fontSize: '13px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                transition: 'all 0.2s'
                            }}
                        >
                            <Mail size={16} /> Disparo de E-mail Marketing
                            <span style={{
                                backgroundColor: broadcastSubTab === 'email' ? '#fae8ff' : '#0284c7',
                                color: broadcastSubTab === 'email' ? '#0369a1' : 'white',
                                fontSize: '10px',
                                fontWeight: '800',
                                padding: '2px 6px',
                                borderRadius: '999px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.04em'
                            }}>
                                Resend
                            </span>
                        </button>
                    </div>

                    {/* SUB-ABA 0: CAMPANHA SEMANAL DE ATIVAÇÃO & UPGRADE (AUTOMÁTICA / INTELIGENTE) */}
                    {broadcastSubTab === 'weekly' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                            {/* Card Superior: Descrição & Status da Automação Semanal */}
                            <div style={{
                                background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)',
                                padding: '22px 26px',
                                borderRadius: '20px',
                                border: '1px solid #d8b4fe',
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '16px'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '14px', maxWidth: '750px' }}>
                                    <div style={{
                                        backgroundColor: '#9333ea',
                                        color: 'white',
                                        padding: '12px',
                                        borderRadius: '14px',
                                        boxShadow: '0 4px 12px rgba(147, 51, 234, 0.25)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <Sparkles size={24} />
                                    </div>
                                    <div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                                            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#581c87' }}>
                                                Campanha Semanal Inteligente de Ativação & Upgrade
                                            </h3>
                                            <span style={{
                                                backgroundColor: '#dcfce7',
                                                color: '#166534',
                                                fontSize: '11px',
                                                fontWeight: '800',
                                                padding: '2px 8px',
                                                borderRadius: '999px'
                                            }}>
                                                Instância B (WhatsApp Oficial)
                                            </span>
                                        </div>
                                        <p style={{ margin: 0, fontSize: '13px', color: '#6b21a8', lineHeight: '1.5' }}>
                                            Dispara mensagens estratégicas personalizadas para usuários do plano Free com WhatsApp verificado. O robô detecta automaticamente quem já configurou APIs de afiliados e quem ainda não configurou, enviando a mensagem correspondente e respeitando o limite de <strong>1 envio a cada 7 dias</strong> por usuário.
                                        </p>
                                    </div>
                                </div>

                                {/* Toggle de Automação Semanal */}
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    backgroundColor: 'white',
                                    padding: '10px 16px',
                                    borderRadius: '14px',
                                    border: '1px solid #e9d5ff',
                                    boxShadow: '0 2px 6px rgba(0,0,0,0.03)'
                                }}>
                                    <div style={{ textAlign: 'right' }}>
                                        <div style={{ fontSize: '12px', fontWeight: '700', color: '#1e293b' }}>
                                            Agendamento Semanal
                                        </div>
                                        <div style={{ fontSize: '11px', color: '#64748b' }}>
                                            Segundas às 10:00
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleToggleWeeklyActive}
                                        style={{
                                            padding: '7px 14px',
                                            borderRadius: '10px',
                                            border: 'none',
                                            backgroundColor: weeklyStats?.templates?.active ? '#22c55e' : '#94a3b8',
                                            color: 'white',
                                            fontSize: '12px',
                                            fontWeight: '700',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        <Zap size={14} />
                                        {weeklyStats?.templates?.active ? 'Ativado' : 'Pausado'}
                                    </button>
                                </div>
                            </div>

                            {/* Cards de Métricas em Tempo Real */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
                                {/* Card 1: Elegíveis Upgrade (Com API) */}
                                <div style={{
                                    backgroundColor: 'white',
                                    padding: '18px 20px',
                                    borderRadius: '16px',
                                    border: '1px solid #e2e8f0',
                                    borderLeft: '4px solid #9333ea',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                                        <span style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>
                                            Elegíveis para Upgrade
                                        </span>
                                        <div style={{ backgroundColor: '#f3e8ff', color: '#9333ea', padding: '6px', borderRadius: '8px' }}>
                                            <TrendingUp size={16} />
                                        </div>
                                    </div>
                                    <div style={{ fontSize: '28px', fontWeight: '800', color: '#1e293b' }}>
                                        {loadingWeeklyStats ? '...' : (weeklyStats?.counts?.eligibleUpgrade ?? 0)}
                                    </div>
                                    <div style={{ fontSize: '11px', color: '#9333ea', fontWeight: '600', marginTop: '4px' }}>
                                        Com API cadastrada (Shopee, ML...)
                                    </div>
                                </div>

                                {/* Card 2: Elegíveis Ativação (Sem API) */}
                                <div style={{
                                    backgroundColor: 'white',
                                    padding: '18px 20px',
                                    borderRadius: '16px',
                                    border: '1px solid #e2e8f0',
                                    borderLeft: '4px solid #3b82f6',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                                        <span style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>
                                            Elegíveis para Ativação
                                        </span>
                                        <div style={{ backgroundColor: '#eff6ff', color: '#3b82f6', padding: '6px', borderRadius: '8px' }}>
                                            <Key size={16} />
                                        </div>
                                    </div>
                                    <div style={{ fontSize: '28px', fontWeight: '800', color: '#1e293b' }}>
                                        {loadingWeeklyStats ? '...' : (weeklyStats?.counts?.eligibleActivation ?? 0)}
                                    </div>
                                    <div style={{ fontSize: '11px', color: '#3b82f6', fontWeight: '600', marginTop: '4px' }}>
                                        Ainda sem APIs configuradas
                                    </div>
                                </div>

                                {/* Card 3: Em Cooldown (< 7 dias) */}
                                <div style={{
                                    backgroundColor: 'white',
                                    padding: '18px 20px',
                                    borderRadius: '16px',
                                    border: '1px solid #e2e8f0',
                                    borderLeft: '4px solid #64748b',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                                        <span style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>
                                            Em Cooldown (&lt; 7 dias)
                                        </span>
                                        <div style={{ backgroundColor: '#f1f5f9', color: '#64748b', padding: '6px', borderRadius: '8px' }}>
                                            <Clock size={16} />
                                        </div>
                                    </div>
                                    <div style={{ fontSize: '28px', fontWeight: '800', color: '#1e293b' }}>
                                        {loadingWeeklyStats ? '...' : (weeklyStats?.counts?.inCooldown ?? 0)}
                                    </div>
                                    <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '600', marginTop: '4px' }}>
                                        Já receberam nos últimos 7 dias
                                    </div>
                                </div>

                                {/* Card 4: Total Free Verificados */}
                                <div style={{
                                    backgroundColor: 'white',
                                    padding: '18px 20px',
                                    borderRadius: '16px',
                                    border: '1px solid #e2e8f0',
                                    borderLeft: '4px solid #10b981',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                                        <span style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>
                                            Total Free Verificados
                                        </span>
                                        <div style={{ backgroundColor: '#ecfdf5', color: '#10b981', padding: '6px', borderRadius: '8px' }}>
                                            <Users size={16} />
                                        </div>
                                    </div>
                                    <div style={{ fontSize: '28px', fontWeight: '800', color: '#1e293b' }}>
                                        {loadingWeeklyStats ? '...' : (weeklyStats?.counts?.totalFreeVerified ?? 0)}
                                    </div>
                                    <div style={{ fontSize: '11px', color: '#10b981', fontWeight: '600', marginTop: '4px' }}>
                                        Base total com WhatsApp confirmado
                                    </div>
                                </div>
                            </div>

                            {/* Grid Principal: Editor de Mensagens (Esquerda) vs Prévia & Disparo (Direita) */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '24px', alignItems: 'start' }}>
                                {/* Coluna Esquerda: Editor de Mensagens */}
                                <div style={{
                                    backgroundColor: 'white',
                                    padding: '24px',
                                    borderRadius: '20px',
                                    border: '1px solid #e2e8f0',
                                    boxShadow: '0 4px 15px rgba(0,0,0,0.03)'
                                }}>
                                    {/* Tabs de Seleção de Mensagem */}
                                    <div style={{ display: 'flex', gap: '8px', marginBottom: '18px', backgroundColor: '#f1f5f9', padding: '4px', borderRadius: '12px' }}>
                                        <button
                                            type="button"
                                            onClick={() => setWeeklyTab('upgrade')}
                                            style={{
                                                flex: 1,
                                                padding: '10px',
                                                borderRadius: '8px',
                                                border: 'none',
                                                backgroundColor: weeklyTab === 'upgrade' ? '#9333ea' : 'transparent',
                                                color: weeklyTab === 'upgrade' ? 'white' : '#475569',
                                                fontWeight: '700',
                                                fontSize: '13px',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '6px',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            <TrendingUp size={16} />
                                            1. Upgrade (Com API)
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setWeeklyTab('activation')}
                                            style={{
                                                flex: 1,
                                                padding: '10px',
                                                borderRadius: '8px',
                                                border: 'none',
                                                backgroundColor: weeklyTab === 'activation' ? '#9333ea' : 'transparent',
                                                color: weeklyTab === 'activation' ? 'white' : '#475569',
                                                fontWeight: '700',
                                                fontSize: '13px',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '6px',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            <Key size={16} />
                                            2. Ativação (Sem API)
                                        </button>
                                    </div>

                                    {/* Banner contextual da mensagem */}
                                    <div style={{
                                        padding: '10px 14px',
                                        borderRadius: '10px',
                                        marginBottom: '14px',
                                        backgroundColor: weeklyTab === 'upgrade' ? '#faf5ff' : '#eff6ff',
                                        border: `1px solid ${weeklyTab === 'upgrade' ? '#e9d5ff' : '#bfdbfe'}`,
                                        fontSize: '12px',
                                        color: weeklyTab === 'upgrade' ? '#7e22ce' : '#1d4ed8',
                                        lineHeight: '1.4'
                                    }}>
                                        {weeklyTab === 'upgrade' ? (
                                            <>
                                                🎯 <strong>Público:</strong> Usuários Free que já cadastraram pelo menos uma API de afiliado. A mensagem destaca as limitações do Free e o valor dos planos <strong>Basic</strong> e <strong>Premium</strong> no piloto automático.
                                            </>
                                        ) : (
                                            <>
                                                💡 <strong>Público:</strong> Usuários Free que ainda não cadastraram chaves de afiliado. A mensagem ensina como cadastrar Shopee, Mercado Livre e Amazon para começar a lucrar.
                                            </>
                                        )}
                                    </div>

                                    {/* Tags Dinâmicas Clicáveis */}
                                    <div style={{ marginBottom: '12px' }}>
                                        <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '6px' }}>
                                            Tags Dinâmicas (Clique para inserir):
                                        </div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                            {[
                                                { tag: '{nome}', desc: 'Primeiro nome' },
                                                { tag: '{username}', desc: 'Usuário' },
                                                { tag: '{plano}', desc: 'Plano (Free)' },
                                                { tag: '{link}', desc: 'Painel' },
                                                { tag: '{link_upgrade}', desc: 'Planos' },
                                                { tag: '{link_config}', desc: 'Configurações' }
                                            ].map((t) => (
                                                <button
                                                    key={t.tag}
                                                    type="button"
                                                    onClick={() => {
                                                        if (weeklyTab === 'upgrade') {
                                                            setWeeklyMsgUpgrade(prev => prev + ' ' + t.tag);
                                                        } else {
                                                            setWeeklyMsgActivation(prev => prev + ' ' + t.tag);
                                                        }
                                                    }}
                                                    style={{
                                                        padding: '4px 8px',
                                                        borderRadius: '6px',
                                                        border: '1px solid #e2e8f0',
                                                        backgroundColor: '#f8fafc',
                                                        fontSize: '11px',
                                                        color: '#475569',
                                                        fontFamily: 'monospace',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.15s'
                                                    }}
                                                    title={t.desc}
                                                >
                                                    {t.tag}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Textarea da Mensagem */}
                                    <div style={{ marginBottom: '16px' }}>
                                        <textarea
                                            value={weeklyTab === 'upgrade' ? weeklyMsgUpgrade : weeklyMsgActivation}
                                            onChange={(e) => {
                                                if (weeklyTab === 'upgrade') {
                                                    setWeeklyMsgUpgrade(e.target.value);
                                                } else {
                                                    setWeeklyMsgActivation(e.target.value);
                                                }
                                            }}
                                            rows={13}
                                            style={{
                                                width: '100%',
                                                padding: '14px',
                                                borderRadius: '12px',
                                                border: '1px solid #cbd5e1',
                                                fontSize: '13px',
                                                lineHeight: '1.5',
                                                outline: 'none',
                                                resize: 'vertical',
                                                fontFamily: 'inherit',
                                                backgroundColor: '#fff',
                                                boxSizing: 'border-box'
                                            }}
                                            placeholder="Digite o texto da mensagem semanal..."
                                        />
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '6px', fontSize: '11px', color: '#94a3b8' }}>
                                            <span>Suporta formatação do WhatsApp (*negrito*, _itálico_, ~tachado~)</span>
                                            <span>{(weeklyTab === 'upgrade' ? weeklyMsgUpgrade : weeklyMsgActivation).length} caracteres</span>
                                        </div>
                                    </div>

                                    {/* Botões de Ação do Editor */}
                                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between' }}>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (weeklyTab === 'upgrade') {
                                                    setWeeklyMsgUpgrade(DEFAULT_WEEKLY_MSG_UPGRADE);
                                                    toast.success("Mensagem de Upgrade restaurada para o padrão!");
                                                } else {
                                                    setWeeklyMsgActivation(DEFAULT_WEEKLY_MSG_ACTIVATION);
                                                    toast.success("Mensagem de Ativação restaurada para o padrão!");
                                                }
                                            }}
                                            style={{
                                                padding: '10px 16px',
                                                borderRadius: '10px',
                                                border: '1px solid #cbd5e1',
                                                backgroundColor: '#f8fafc',
                                                color: '#475569',
                                                fontSize: '12px',
                                                fontWeight: '600',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px'
                                            }}
                                        >
                                            <RefreshCw size={14} /> Restaurar Padrão
                                        </button>

                                        <button
                                            type="button"
                                            onClick={handleSaveWeeklyConfig}
                                            disabled={savingWeeklyConfig}
                                            style={{
                                                padding: '10px 20px',
                                                borderRadius: '10px',
                                                border: 'none',
                                                backgroundColor: '#9333ea',
                                                color: 'white',
                                                fontSize: '13px',
                                                fontWeight: '700',
                                                cursor: savingWeeklyConfig ? 'not-allowed' : 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                boxShadow: '0 4px 12px rgba(147, 51, 234, 0.25)',
                                                opacity: savingWeeklyConfig ? 0.7 : 1
                                            }}
                                        >
                                            <Save size={15} /> {savingWeeklyConfig ? 'Salvando...' : 'Salvar Textos no Banco'}
                                        </button>
                                    </div>
                                </div>

                                {/* Coluna Direita: Pré-visualização do WhatsApp & Painel de Execução */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                    {/* Mockup Balão WhatsApp */}
                                    <div style={{
                                        backgroundColor: '#efeae2',
                                        padding: '20px',
                                        borderRadius: '20px',
                                        border: '1px solid #e2e8f0',
                                        boxShadow: '0 4px 15px rgba(0,0,0,0.03)'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px', borderBottom: '1px solid rgba(0,0,0,0.06)', paddingBottom: '10px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <Smartphone size={16} color="#059669" />
                                                <span style={{ fontSize: '12px', fontWeight: '700', color: '#1e293b' }}>
                                                    Pré-visualização no WhatsApp ({weeklyTab === 'upgrade' ? 'Upgrade' : 'Ativação'})
                                                </span>
                                            </div>
                                            <span style={{ fontSize: '11px', color: '#059669', fontWeight: '700', backgroundColor: '#d1fae5', padding: '2px 8px', borderRadius: '12px' }}>
                                                Instância B
                                            </span>
                                        </div>

                                        {/* Balão de Conversa */}
                                        <div style={{
                                            backgroundColor: '#ffffff',
                                            borderRadius: '12px',
                                            borderTopLeftRadius: '2px',
                                            padding: '14px 16px',
                                            boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                            fontSize: '13px',
                                            lineHeight: '1.5',
                                            color: '#111827',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            position: 'relative'
                                        }}>
                                            {(weeklyTab === 'upgrade' ? weeklyMsgUpgrade : weeklyMsgActivation)
                                                .replace(/{nome}/g, 'Carlos')
                                                .replace(/{username}/g, 'carlos.afiliado')
                                                .replace(/{plano}/g, 'Free')
                                                .replace(/{link_upgrade}/g, 'https://anunciosapp.com.br/planos')
                                                .replace(/{link_config}/g, 'https://anunciosapp.com.br/config')
                                                .replace(/{link}/g, 'https://anunciosapp.com.br')
                                            }
                                            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '4px', marginTop: '6px', fontSize: '10px', color: '#6b7280' }}>
                                                <span>10:00</span>
                                                <span style={{ color: '#38bdf8', fontWeight: 'bold' }}>✓✓</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Card: Teste no WhatsApp Pessoal */}
                                    <div style={{
                                        backgroundColor: 'white',
                                        padding: '20px',
                                        borderRadius: '16px',
                                        border: '1px solid #e2e8f0',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
                                    }}>
                                        <h4 style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#1e293b', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <Send size={15} color="#9333ea" />
                                            Enviar Mensagem de Teste no seu WhatsApp
                                        </h4>
                                        <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: '#64748b' }}>
                                            Receba no seu celular a mensagem de <strong>{weeklyTab === 'upgrade' ? 'Upgrade' : 'Ativação'}</strong> exatamente como o cliente verá.
                                        </p>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <input
                                                type="text"
                                                placeholder="5511999999999 (com DDD)"
                                                value={weeklyTestNumber}
                                                onChange={(e) => setWeeklyTestNumber(e.target.value)}
                                                style={{
                                                    flex: 1,
                                                    padding: '9px 12px',
                                                    borderRadius: '10px',
                                                    border: '1px solid #cbd5e1',
                                                    fontSize: '13px',
                                                    outline: 'none'
                                                }}
                                            />
                                            <button
                                                type="button"
                                                onClick={handleSendWeeklyTest}
                                                disabled={sendingWeeklyTest}
                                                style={{
                                                    padding: '9px 16px',
                                                    borderRadius: '10px',
                                                    border: 'none',
                                                    backgroundColor: '#10b981',
                                                    color: 'white',
                                                    fontSize: '12px',
                                                    fontWeight: '700',
                                                    cursor: sendingWeeklyTest ? 'not-allowed' : 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    whiteSpace: 'nowrap',
                                                    opacity: sendingWeeklyTest ? 0.7 : 1
                                                }}
                                            >
                                                <Send size={13} /> {sendingWeeklyTest ? 'Enviando...' : 'Enviar Teste'}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Card: Painel de Disparo em Lote */}
                                    <div style={{
                                        backgroundColor: 'white',
                                        padding: '22px',
                                        borderRadius: '16px',
                                        border: '1px solid #e2e8f0',
                                        boxShadow: '0 4px 15px rgba(0,0,0,0.03)'
                                    }}>
                                        <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#1e293b', fontWeight: '800' }}>
                                            Execução da Campanha Semanal
                                        </h4>

                                        {/* Status de Envio Ativo */}
                                        {weeklyStats?.sendingStatus?.sending ? (
                                            <div style={{
                                                padding: '16px',
                                                borderRadius: '12px',
                                                backgroundColor: '#fdf4ff',
                                                border: '1px solid #f0abfc',
                                                marginBottom: '16px'
                                            }}>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                                                    <span style={{ fontSize: '13px', fontWeight: '800', color: '#a21caf', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                        <RefreshCw size={15} className="spin" /> Disparo em Andamento...
                                                    </span>
                                                    <span style={{ fontSize: '12px', fontWeight: '700', color: '#a21caf' }}>
                                                        {weeklyStats.sendingStatus.total > 0
                                                            ? Math.round(((weeklyStats.sendingStatus.sent + weeklyStats.sendingStatus.failed) / weeklyStats.sendingStatus.total) * 100)
                                                            : 0}%
                                                    </span>
                                                </div>

                                                {/* Barra de Progresso */}
                                                <div style={{ width: '100%', height: '8px', backgroundColor: '#e9d5ff', borderRadius: '4px', overflow: 'hidden', marginBottom: '10px' }}>
                                                    <div style={{
                                                        width: `${weeklyStats.sendingStatus.total > 0 ? ((weeklyStats.sendingStatus.sent + weeklyStats.sendingStatus.failed) / weeklyStats.sendingStatus.total) * 100 : 0}%`,
                                                        height: '100%',
                                                        backgroundColor: '#9333ea',
                                                        transition: 'width 0.3s'
                                                    }} />
                                                </div>

                                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#701a75', marginBottom: '14px' }}>
                                                    <span>Enviados: <strong>{weeklyStats.sendingStatus.sent}</strong> de <strong>{weeklyStats.sendingStatus.total}</strong></span>
                                                    <span>Falhas: <strong>{weeklyStats.sendingStatus.failed}</strong></span>
                                                </div>

                                                <button
                                                    type="button"
                                                    onClick={handleStopWeeklyCampaign}
                                                    style={{
                                                        width: '100%',
                                                        padding: '10px',
                                                        borderRadius: '10px',
                                                        border: 'none',
                                                        backgroundColor: '#ef4444',
                                                        color: 'white',
                                                        fontWeight: '700',
                                                        fontSize: '13px',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: '6px'
                                                    }}
                                                >
                                                    <StopCircle size={16} /> Interromper Disparo
                                                </button>
                                            </div>
                                        ) : (
                                            <>
                                                {/* Delays Anti-ban */}
                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
                                                    <div>
                                                        <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#64748b', marginBottom: '4px' }}>
                                                            Intervalo Mínimo (s)
                                                        </label>
                                                        <input
                                                            type="number"
                                                            min="5"
                                                            max="60"
                                                            value={weeklyMinDelay}
                                                            onChange={(e) => setWeeklyMinDelay(Number(e.target.value))}
                                                            style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }}
                                                        />
                                                    </div>
                                                    <div>
                                                        <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#64748b', marginBottom: '4px' }}>
                                                            Intervalo Máximo (s)
                                                        </label>
                                                        <input
                                                            type="number"
                                                            min="8"
                                                            max="120"
                                                            value={weeklyMaxDelay}
                                                            onChange={(e) => setWeeklyMaxDelay(Number(e.target.value))}
                                                            style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }}
                                                        />
                                                    </div>
                                                </div>

                                                <p style={{ margin: '0 0 16px 0', fontSize: '12px', color: '#64748b', lineHeight: '1.4' }}>
                                                    Ao iniciar, o robô enviará a <strong>Mensagem de Upgrade</strong> para {weeklyStats?.counts?.eligibleUpgrade || 0} usuários com API e a <strong>Mensagem de Ativação</strong> para {weeklyStats?.counts?.eligibleActivation || 0} usuários sem API, com pausa anti-ban entre cada mensagem.
                                                </p>

                                                <button
                                                    type="button"
                                                    onClick={handleStartWeeklyCampaign}
                                                    disabled={sendingWeeklyCampaign || ((weeklyStats?.counts?.eligibleUpgrade || 0) + (weeklyStats?.counts?.eligibleActivation || 0) === 0)}
                                                    style={{
                                                        width: '100%',
                                                        padding: '14px',
                                                        borderRadius: '12px',
                                                        border: 'none',
                                                        background: ((weeklyStats?.counts?.eligibleUpgrade || 0) + (weeklyStats?.counts?.eligibleActivation || 0) === 0)
                                                            ? '#cbd5e1'
                                                            : 'linear-gradient(135deg, #9333ea 0%, #7e22ce 100%)',
                                                        color: 'white',
                                                        fontSize: '14px',
                                                        fontWeight: '800',
                                                        cursor: ((weeklyStats?.counts?.eligibleUpgrade || 0) + (weeklyStats?.counts?.eligibleActivation || 0) === 0) ? 'not-allowed' : 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: '8px',
                                                        boxShadow: '0 4px 15px rgba(147, 51, 234, 0.3)',
                                                        transition: 'all 0.2s'
                                                    }}
                                                >
                                                    <Play size={18} /> Iniciar Campanha Semanal Agora ({ (weeklyStats?.counts?.eligibleUpgrade || 0) + (weeklyStats?.counts?.eligibleActivation || 0) } Elegíveis)
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* SUB-ABA 1: DISPARO PARA USUÁRIOS CADASTRADOS (ONBOARDING & COMUNICAÇÃO) */}
                    {broadcastSubTab === 'users' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '28px', alignItems: 'start' }}>
                                {/* Coluna Esquerda: Configuração da Campanha */}
                                <div style={{ background: 'white', padding: '24px', borderRadius: '20px', boxShadow: '0 4px 15px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '18px' }}>
                                        <div style={{ backgroundColor: '#f3e8ff', padding: '8px', borderRadius: '10px' }}>
                                            <MessageCircle size={20} color="#9333ea" />
                                        </div>
                                        <div>
                                            <h3 style={{ margin: 0, fontSize: '16px', color: '#1e293b' }}>Campanha WhatsApp para Usuários</h3>
                                            <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>Envio direto no WhatsApp dos afiliados cadastrados (Instância B)</p>
                                        </div>
                                    </div>

                                    {/* Alerta de Instância B desconectada */}
                                    {whatsStatusB !== 'Conectado' && (
                                        <div style={{
                                            backgroundColor: '#fef2f2',
                                            border: '1px solid #fecaca',
                                            borderRadius: '12px',
                                            padding: '12px 14px',
                                            marginBottom: '16px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: '12px'
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <AlertCircle size={20} color="#dc2626" />
                                                <div>
                                                    <div style={{ fontSize: '12px', fontWeight: '700', color: '#991b1b' }}>Instância B Desconectada</div>
                                                    <div style={{ fontSize: '11px', color: '#b91c1c' }}>Conecte a Instância B nas configurações para poder disparar.</div>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => setActiveTab('whatsapp_instances')}
                                                style={{
                                                    backgroundColor: '#dc2626',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '8px',
                                                    padding: '6px 12px',
                                                    fontSize: '11px',
                                                    fontWeight: '700',
                                                    cursor: 'pointer',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                Conectar Agora
                                            </button>
                                        </div>
                                    )}

                                    <form onSubmit={handleStartUserBroadcast} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                        {/* Modelos Prontos */}
                                        <div>
                                            <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                                Modelos Rápidos de Mensagem
                                            </label>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                                {USER_TEMPLATES.map((tmpl) => (
                                                    <button
                                                        key={tmpl.id}
                                                        type="button"
                                                        onClick={() => setUserBroadcastMessage(tmpl.text)}
                                                        style={{
                                                            padding: '9px 10px',
                                                            borderRadius: '8px',
                                                            border: '1px solid #e2e8f0',
                                                            backgroundColor: '#f8fafc',
                                                            color: '#334155',
                                                            fontSize: '11px',
                                                            fontWeight: '600',
                                                            cursor: 'pointer',
                                                            textAlign: 'left',
                                                            transition: 'all 0.15s'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.borderColor = '#c084fc';
                                                            e.currentTarget.style.backgroundColor = '#faf5ff';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.borderColor = '#e2e8f0';
                                                            e.currentTarget.style.backgroundColor = '#f8fafc';
                                                        }}
                                                    >
                                                        {tmpl.title}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Filtros de Segmentação */}
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                            <div>
                                                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                                                    Filtrar por Plano
                                                </label>
                                                <select
                                                    value={userFilterPlan}
                                                    onChange={(e) => setUserFilterPlan(e.target.value)}
                                                    style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', outline: 'none', backgroundColor: '#fff' }}
                                                    disabled={sendingUserBroadcast}
                                                >
                                                    <option value="all">Todos os Planos</option>
                                                    <option value="free">Apenas Free (Amostra / Gratuitos)</option>
                                                    <option value="basic">Apenas Basic</option>
                                                    <option value="premium">Apenas Premium</option>
                                                    <option value="divulgador">Apenas Divulgadores</option>
                                                </select>
                                            </div>

                                            <div>
                                                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                                                    Público-Alvo
                                                </label>
                                                <select
                                                    value={userFilterAudience}
                                                    onChange={(e) => setUserFilterAudience(e.target.value)}
                                                    style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', outline: 'none', backgroundColor: '#fff' }}
                                                    disabled={sendingUserBroadcast}
                                                >
                                                    <option value="all">Todos com WhatsApp informado</option>
                                                    <option value="verified_only">Apenas WhatsApp Verificado</option>
                                                    <option value="recent_7_days">Recém-cadastrados (Últimos 7 dias)</option>
                                                </select>
                                            </div>
                                        </div>

                                        {/* Inserção de Tags Dinâmicas */}
                                        <div>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                                                <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569' }}>
                                                    Texto da Mensagem
                                                </label>
                                                <div style={{ display: 'flex', gap: '4px' }}>
                                                    {['{nome}', '{username}', '{plano}', '{link}'].map((tag) => (
                                                        <button
                                                            key={tag}
                                                            type="button"
                                                            onClick={() => setUserBroadcastMessage(prev => prev + ' ' + tag)}
                                                            style={{
                                                                padding: '2px 6px',
                                                                borderRadius: '4px',
                                                                border: '1px solid #cbd5e1',
                                                                backgroundColor: '#f1f5f9',
                                                                color: '#64748b',
                                                                fontSize: '11px',
                                                                cursor: 'pointer',
                                                                fontWeight: '600'
                                                            }}
                                                            title={`Inserir ${tag}`}
                                                        >
                                                            {tag}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>

                                            <textarea
                                                value={userBroadcastMessage}
                                                onChange={(e) => setUserBroadcastMessage(e.target.value)}
                                                rows={6}
                                                style={{
                                                    width: '100%',
                                                    padding: '12px',
                                                    borderRadius: '10px',
                                                    border: '1px solid #cbd5e1',
                                                    fontSize: '13px',
                                                    lineHeight: '1.5',
                                                    outline: 'none',
                                                    resize: 'vertical',
                                                    boxSizing: 'border-box',
                                                    fontFamily: 'inherit'
                                                }}
                                                placeholder="Digite sua mensagem aqui..."
                                                disabled={sendingUserBroadcast}
                                            />
                                        </div>

                                        {/* Intervalo Anti-Ban (Delay aleatório) */}
                                        <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                                                <Shield size={15} color="#10b981" />
                                                <span style={{ fontSize: '12px', fontWeight: '700', color: '#1e293b' }}>
                                                    Proteção Anti-Ban WhatsApp (Intervalo Seguro)
                                                </span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <span style={{ fontSize: '12px', color: '#64748b' }}>Entre</span>
                                                <input
                                                    type="number"
                                                    min="4"
                                                    max="60"
                                                    value={userMinDelay}
                                                    onChange={(e) => setUserMinDelay(e.target.value)}
                                                    style={{ width: '60px', padding: '6px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '12px', textAlign: 'center' }}
                                                    disabled={sendingUserBroadcast}
                                                />
                                                <span style={{ fontSize: '12px', color: '#64748b' }}>e</span>
                                                <input
                                                    type="number"
                                                    min="5"
                                                    max="90"
                                                    value={userMaxDelay}
                                                    onChange={(e) => setUserMaxDelay(e.target.value)}
                                                    style={{ width: '60px', padding: '6px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '12px', textAlign: 'center' }}
                                                    disabled={sendingUserBroadcast}
                                                />
                                                <span style={{ fontSize: '12px', color: '#64748b' }}>segundos por usuário</span>
                                            </div>
                                        </div>

                                        {/* Preview WhatsApp */}
                                        <div>
                                            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                                                Pré-visualização (Como o afiliado verá no WhatsApp)
                                            </label>
                                            <div style={{
                                                backgroundColor: '#e5ddd5',
                                                borderRadius: '12px',
                                                padding: '16px',
                                                backgroundImage: 'radial-gradient(#cfc6bc 1px, transparent 1px)',
                                                backgroundSize: '16px 16px'
                                            }}>
                                                <div style={{
                                                    backgroundColor: '#dcf8c6',
                                                    borderRadius: '10px 10px 0 10px',
                                                    padding: '12px 14px',
                                                    fontSize: '13px',
                                                    color: '#111827',
                                                    lineHeight: '1.4',
                                                    whiteSpace: 'pre-wrap',
                                                    maxWidth: '92%',
                                                    boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                                                    marginLeft: 'auto'
                                                }}>
                                                    {userBroadcastMessage
                                                        .replace(/{nome}/gi, 'Vinícius')
                                                        .replace(/{username}/gi, 'viniciusa')
                                                        .replace(/{plano}/gi, 'PREMIUM')
                                                        .replace(/{link}/gi, 'https://anunciosapp.com.br')}
                                                    <div style={{ textAlign: 'right', fontSize: '10px', color: '#65676b', marginTop: '4px' }}>
                                                        {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })} ✓✓
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Teste antes de disparar */}
                                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                            <input
                                                type="text"
                                                placeholder="WhatsApp de teste (ex: 11999999999)"
                                                value={userTestNumber}
                                                onChange={(e) => setUserTestNumber(e.target.value)}
                                                style={{ flex: 1, padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '12px', outline: 'none' }}
                                                disabled={sendingUserBroadcast || testingUserMessage}
                                            />
                                            <button
                                                type="button"
                                                onClick={handleSendTestUserMessage}
                                                disabled={sendingUserBroadcast || testingUserMessage}
                                                style={{
                                                    padding: '10px 14px',
                                                    borderRadius: '8px',
                                                    border: '1px solid #9333ea',
                                                    backgroundColor: '#faf5ff',
                                                    color: '#9333ea',
                                                    fontSize: '12px',
                                                    fontWeight: '700',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                <Smartphone size={15} /> Testar Envio
                                            </button>
                                        </div>

                                        {/* Botão Principal de Disparo */}
                                        <button
                                            type="submit"
                                            disabled={sendingUserBroadcast || whatsStatusB !== 'Conectado' || selectedUserIds.length === 0}
                                            style={{
                                                backgroundColor: (sendingUserBroadcast || whatsStatusB !== 'Conectado' || selectedUserIds.length === 0) ? '#cbd5e1' : '#9333ea',
                                                color: '#fff',
                                                border: 'none',
                                                borderRadius: '10px',
                                                padding: '14px',
                                                fontSize: '14px',
                                                fontWeight: '700',
                                                cursor: (sendingUserBroadcast || whatsStatusB !== 'Conectado' || selectedUserIds.length === 0) ? 'not-allowed' : 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '8px',
                                                boxShadow: (sendingUserBroadcast || whatsStatusB !== 'Conectado' || selectedUserIds.length === 0) ? 'none' : '0 4px 12px rgba(147, 51, 234, 0.3)',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            <Send size={18} />
                                            <span>
                                                {sendingUserBroadcast
                                                    ? 'Disparando Campanha...'
                                                    : selectedUserIds.length === 0
                                                        ? 'Selecione Afiliados na Lista Abaixo'
                                                        : `Iniciar Campanha para ${selectedUserIds.length} Afiliado(s) Selecionado(s)`}
                                            </span>
                                        </button>
                                    </form>
                                </div>

                                {/* Coluna Direita: Progresso ao Vivo e Histórico */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                    {/* Painel de Progresso ao Vivo */}
                                    <div style={{ background: 'white', padding: '24px', borderRadius: '20px', boxShadow: '0 4px 15px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                            <h3 style={{ margin: 0, fontSize: '15px', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <RefreshCw size={17} color="#9333ea" className={userBroadcastStatus?.running ? "custom-spin" : ""} />
                                                Status do Disparador
                                            </h3>
                                            <span style={{
                                                padding: '4px 10px',
                                                borderRadius: '6px',
                                                fontSize: '11px',
                                                fontWeight: '700',
                                                textTransform: 'uppercase',
                                                backgroundColor: userBroadcastStatus?.running ? '#fef3c7' : (whatsStatusB === 'Conectado' ? '#dcfce7' : '#fee2e2'),
                                                color: userBroadcastStatus?.running ? '#b45309' : (whatsStatusB === 'Conectado' ? '#15803d' : '#b91c1c')
                                            }}>
                                                {userBroadcastStatus?.running ? 'Disparo em Andamento' : (whatsStatusB === 'Conectado' ? 'Instância B Pronta' : 'Instância B Offline')}
                                            </span>
                                        </div>

                                        {userBroadcastStatus?.running ? (
                                            <div>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                                                    <span>Progresso: {userBroadcastStatus.current} de {userBroadcastStatus.total} usuários</span>
                                                    <span>{Math.round((userBroadcastStatus.current / (userBroadcastStatus.total || 1)) * 100)}%</span>
                                                </div>

                                                {/* Barra de Progresso */}
                                                <div style={{ height: '10px', backgroundColor: '#f1f5f9', borderRadius: '5px', overflow: 'hidden', marginBottom: '14px' }}>
                                                    <div style={{
                                                        height: '100%',
                                                        backgroundColor: '#9333ea',
                                                        width: `${Math.round((userBroadcastStatus.current / (userBroadcastStatus.total || 1)) * 100)}%`,
                                                        transition: 'width 0.4s ease'
                                                    }} />
                                                </div>

                                                {userBroadcastStatus.currentUser && (
                                                    <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '14px' }}>
                                                        Enviando para: <strong>{userBroadcastStatus.currentUser}</strong>
                                                    </div>
                                                )}

                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
                                                    <div style={{ backgroundColor: '#f0fdf4', padding: '10px', borderRadius: '8px', border: '1px solid #bbf7d0', textAlign: 'center' }}>
                                                        <span style={{ fontSize: '11px', color: '#15803d', fontWeight: '600' }}>Sucessos</span>
                                                        <div style={{ fontSize: '18px', fontWeight: '800', color: '#16a34a' }}>{userBroadcastStatus.sucessos}</div>
                                                    </div>
                                                    <div style={{ backgroundColor: '#fef2f2', padding: '10px', borderRadius: '8px', border: '1px solid #fecaca', textAlign: 'center' }}>
                                                        <span style={{ fontSize: '11px', color: '#b91c1c', fontWeight: '600' }}>Falhas</span>
                                                        <div style={{ fontSize: '18px', fontWeight: '800', color: '#dc2626' }}>{userBroadcastStatus.falhas}</div>
                                                    </div>
                                                </div>

                                                <button
                                                    type="button"
                                                    onClick={handleStopUserBroadcast}
                                                    style={{
                                                        width: '100%',
                                                        padding: '10px',
                                                        borderRadius: '8px',
                                                        border: '1px solid #ef4444',
                                                        backgroundColor: '#fff',
                                                        color: '#ef4444',
                                                        fontWeight: '700',
                                                        fontSize: '13px',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: '6px'
                                                    }}
                                                >
                                                    <StopCircle size={16} /> Interromper Disparo Agora
                                                </button>
                                            </div>
                                        ) : whatsStatusB === 'Conectado' ? (
                                            <div style={{ textAlign: 'center', padding: '20px 10px', color: '#64748b' }}>
                                                <CheckCircle size={36} color="#10b981" style={{ marginBottom: '10px' }} />
                                                <p style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>
                                                    Disparador de WhatsApp Pronto
                                                </p>
                                                <p style={{ margin: '4px 0 0 0', fontSize: '12px' }}>
                                                    A Instância B está conectada e enviará as mensagens com intervalos humanos para proteger o número contra bloqueios.
                                                </p>
                                            </div>
                                        ) : (
                                            <div style={{ textAlign: 'center', padding: '20px 10px', color: '#64748b' }}>
                                                <div style={{ fontSize: '32px', marginBottom: '8px' }}>🔴</div>
                                                <p style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: '#dc2626' }}>
                                                    Instância B Desconectada
                                                </p>
                                                <p style={{ margin: '4px 0 14px 0', fontSize: '12px', color: '#64748b' }}>
                                                    Conecte a Instância B (Suporte / Sistema) na aba de instâncias para enviar mensagens aos afiliados.
                                                </p>
                                                <button
                                                    type="button"
                                                    onClick={() => setActiveTab('whatsapp_instances')}
                                                    style={{
                                                        backgroundColor: '#9333ea',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '8px',
                                                        padding: '8px 16px',
                                                        fontSize: '12px',
                                                        fontWeight: '700',
                                                        cursor: 'pointer',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '6px'
                                                    }}
                                                >
                                                    <MessageCircle size={15} /> Ir para Instâncias WhatsApp
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    {/* Histórico de Campanhas para Usuários */}
                                    <div style={{ background: 'white', padding: '24px', borderRadius: '20px', boxShadow: '0 4px 15px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                            <h3 style={{ margin: 0, fontSize: '15px', color: '#1e293b' }}>Histórico de Campanhas (Usuários)</h3>
                                            <button
                                                type="button"
                                                onClick={fetchUserBroadcastLogs}
                                                disabled={loadingUserLogs}
                                                style={{ background: 'none', border: 'none', color: '#9333ea', fontSize: '12px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                                            >
                                                <RefreshCw size={13} className={loadingUserLogs ? "custom-spin" : ""} /> Atualizar
                                            </button>
                                        </div>

                                        {loadingUserLogs ? (
                                            <div style={{ textAlign: 'center', padding: '30px' }}>
                                                <RefreshCw size={24} color="#9333ea" className="custom-spin" />
                                            </div>
                                        ) : userBroadcastLogs.length === 0 ? (
                                            <div style={{ textAlign: 'center', padding: '30px 10px', color: '#94a3b8', fontSize: '13px' }}>
                                                Nenhum disparo para usuários registrado ainda.
                                            </div>
                                        ) : (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '350px', overflowY: 'auto' }}>
                                                {userBroadcastLogs.map((log) => (
                                                    <div
                                                        key={log.id}
                                                        style={{
                                                            backgroundColor: '#f8fafc',
                                                            border: '1px solid #e2e8f0',
                                                            borderRadius: '10px',
                                                            padding: '12px'
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                                            <span style={{
                                                                fontSize: '10px',
                                                                fontWeight: '700',
                                                                padding: '2px 6px',
                                                                borderRadius: '4px',
                                                                backgroundColor: log.status === 'sucesso' ? '#dcfce7' : '#fef3c7',
                                                                color: log.status === 'sucesso' ? '#15803d' : '#b45309',
                                                                textTransform: 'uppercase'
                                                            }}>
                                                                {log.status === 'sucesso' ? 'Sucesso' : 'Parcial'}
                                                            </span>
                                                            <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                                                                {new Date(log.createdAt).toLocaleDateString('pt-BR')} {new Date(log.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                                            </span>
                                                        </div>
                                                        <p style={{ margin: 0, fontSize: '12px', color: '#334155', lineHeight: '1.4' }}>
                                                            {log.detalhes}
                                                        </p>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* SEÇÃO COMPLETA: SELEÇÃO DE AFILIADOS / DESTINATÁRIOS */}
                            <div style={{ background: 'white', padding: '24px', borderRadius: '20px', boxShadow: '0 4px 15px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px', marginBottom: '20px' }}>
                                    <div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <div style={{ backgroundColor: '#f3e8ff', padding: '8px', borderRadius: '10px' }}>
                                                <Users size={20} color="#9333ea" />
                                            </div>
                                            <div>
                                                <h3 style={{ margin: 0, fontSize: '16px', color: '#1e293b' }}>Destinatários da Campanha (Afiliados Cadastrados)</h3>
                                                <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#64748b' }}>
                                                    Selecione os afiliados que receberão a mensagem via WhatsApp. Apenas afiliados com número cadastrado podem ser marcados.
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Badges de Contagem e Botão Atualizar */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                        <span style={{
                                            padding: '6px 12px',
                                            borderRadius: '8px',
                                            fontSize: '12px',
                                            fontWeight: '700',
                                            backgroundColor: '#f1f5f9',
                                            color: '#475569'
                                        }}>
                                            Total: {filteredUsersForBroadcast.length}
                                        </span>
                                        <span style={{
                                            padding: '6px 12px',
                                            borderRadius: '8px',
                                            fontSize: '12px',
                                            fontWeight: '700',
                                            backgroundColor: '#ecfdf5',
                                            color: '#059669'
                                        }}>
                                            Com WhatsApp: {usersWithPhoneInFilter.length}
                                        </span>
                                        <span style={{
                                            padding: '6px 14px',
                                            borderRadius: '8px',
                                            fontSize: '12px',
                                            fontWeight: '800',
                                            backgroundColor: selectedUserIds.length > 0 ? '#f3e8ff' : '#f8fafc',
                                            color: selectedUserIds.length > 0 ? '#9333ea' : '#94a3b8',
                                            border: selectedUserIds.length > 0 ? '1px solid #d8b4fe' : '1px solid #e2e8f0'
                                        }}>
                                            ✓ {selectedUserIds.length} Selecionados
                                        </span>
                                        <button
                                            type="button"
                                            onClick={fetchAdminUsers}
                                            disabled={loadingAdminUsers}
                                            style={{
                                                background: '#f8fafc',
                                                border: '1px solid #cbd5e1',
                                                color: '#475569',
                                                borderRadius: '8px',
                                                padding: '6px 12px',
                                                fontSize: '12px',
                                                fontWeight: '600',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px'
                                            }}
                                        >
                                            <RefreshCw size={13} className={loadingAdminUsers ? "custom-spin" : ""} /> Atualizar
                                        </button>
                                    </div>
                                </div>

                                {/* Barra de Filtros e Ações Rápidas de Seleção */}
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    gap: '12px',
                                    backgroundColor: '#f8fafc',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '12px',
                                    padding: '12px 16px',
                                    marginBottom: '16px'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: '260px' }}>
                                        <div style={{ position: 'relative', width: '100%', maxWidth: '380px' }}>
                                            <Search size={15} color="#94a3b8" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)' }} />
                                            <input
                                                type="text"
                                                placeholder="Buscar por nome, username, e-mail ou WhatsApp..."
                                                value={userSearchTerm}
                                                onChange={(e) => setUserSearchTerm(e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '8px 12px 8px 32px',
                                                    borderRadius: '8px',
                                                    border: '1px solid #cbd5e1',
                                                    fontSize: '12px',
                                                    outline: 'none',
                                                    backgroundColor: '#fff',
                                                    boxSizing: 'border-box'
                                                }}
                                            />
                                        </div>

                                        <select
                                            value={userPhoneFilter}
                                            onChange={(e) => setUserPhoneFilter(e.target.value)}
                                            style={{
                                                padding: '8px 12px',
                                                borderRadius: '8px',
                                                border: '1px solid #cbd5e1',
                                                fontSize: '12px',
                                                outline: 'none',
                                                backgroundColor: '#fff',
                                                color: '#334155'
                                            }}
                                        >
                                            <option value="with_phone">Apenas com WhatsApp cadastrado</option>
                                            <option value="all">Todos os afiliados (com e sem número)</option>
                                            <option value="without_phone">Apenas sem WhatsApp cadastrado</option>
                                        </select>
                                    </div>

                                    {/* Botões de Ação em Massa */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <button
                                            type="button"
                                            onClick={handleToggleSelectAll}
                                            disabled={usersWithPhoneInFilter.length === 0}
                                            style={{
                                                padding: '8px 14px',
                                                borderRadius: '8px',
                                                border: '1px solid #9333ea',
                                                backgroundColor: isAllSelected ? '#9333ea' : '#faf5ff',
                                                color: isAllSelected ? '#ffffff' : '#9333ea',
                                                fontSize: '12px',
                                                fontWeight: '700',
                                                cursor: usersWithPhoneInFilter.length === 0 ? 'not-allowed' : 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                transition: 'all 0.15s'
                                            }}
                                        >
                                            {isAllSelected ? <CheckSquare size={15} /> : <Square size={15} />}
                                            {isAllSelected ? 'Desmarcar Todos os Filtrados' : `Marcar Todos com WhatsApp (${usersWithPhoneInFilter.length})`}
                                        </button>

                                        {selectedUserIds.length > 0 && (
                                            <button
                                                type="button"
                                                onClick={handleClearSelection}
                                                style={{
                                                    padding: '8px 12px',
                                                    borderRadius: '8px',
                                                    border: '1px solid #cbd5e1',
                                                    backgroundColor: '#fff',
                                                    color: '#64748b',
                                                    fontSize: '12px',
                                                    fontWeight: '600',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '5px'
                                                }}
                                            >
                                                <X size={14} /> Limpar Seleção ({selectedUserIds.length})
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Tabela de Usuários com Seleção */}
                                {loadingAdminUsers ? (
                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '50px' }}>
                                        <RefreshCw className="custom-spin" size={32} style={{ color: '#9333ea' }} />
                                    </div>
                                ) : filteredUsersForBroadcast.length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '40px 10px', color: '#94a3b8', fontSize: '13px' }}>
                                        Nenhum afiliado encontrado para os filtros selecionados.
                                    </div>
                                ) : (
                                    <div style={{ overflowX: 'auto', maxHeight: '520px', overflowY: 'auto' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
                                            <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8fafc', zIndex: 1 }}>
                                                <tr style={{ borderBottom: '2px solid #e2e8f0', color: '#64748b' }}>
                                                    <th style={{ padding: '12px 10px', width: '40px', textAlign: 'center' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={isAllSelected}
                                                            onChange={handleToggleSelectAll}
                                                            disabled={usersWithPhoneInFilter.length === 0}
                                                            style={{ cursor: 'pointer', width: '16px', height: '16px', accentColor: '#9333ea' }}
                                                            title="Marcar/Desmarcar todos os visíveis com WhatsApp"
                                                        />
                                                    </th>
                                                    <th style={{ padding: '12px 10px' }}>Afiliado</th>
                                                    <th style={{ padding: '12px 10px' }}>WhatsApp</th>
                                                    <th style={{ padding: '12px 10px' }}>Plano</th>
                                                    <th style={{ padding: '12px 10px' }}>Data de Cadastro</th>
                                                    <th style={{ padding: '12px 10px', textAlign: 'center' }}>Seleção</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredUsersForBroadcast.map((u) => {
                                                    const hasPhone = !!u.userWhatsappNumber;
                                                    const isSelected = selectedUserIds.includes(u.id);

                                                    return (
                                                        <tr
                                                            key={u.id}
                                                            onClick={() => {
                                                                if (hasPhone) handleToggleUserSelect(u.id);
                                                            }}
                                                            style={{
                                                                borderBottom: '1px solid #f1f5f9',
                                                                backgroundColor: isSelected ? '#faf5ff' : 'transparent',
                                                                cursor: hasPhone ? 'pointer' : 'not-allowed',
                                                                transition: 'background-color 0.15s'
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                if (!isSelected && hasPhone) e.currentTarget.style.backgroundColor = '#f8fafc';
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                if (!isSelected && hasPhone) e.currentTarget.style.backgroundColor = 'transparent';
                                                            }}
                                                        >
                                                            <td style={{ padding: '12px 10px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isSelected}
                                                                    disabled={!hasPhone}
                                                                    onChange={() => hasPhone && handleToggleUserSelect(u.id)}
                                                                    style={{ cursor: hasPhone ? 'pointer' : 'not-allowed', width: '16px', height: '16px', accentColor: '#9333ea' }}
                                                                />
                                                            </td>
                                                            <td style={{ padding: '12px 10px' }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                    <div style={{
                                                                        width: '32px',
                                                                        height: '32px',
                                                                        borderRadius: '50%',
                                                                        backgroundColor: isSelected ? '#9333ea' : '#e2e8f0',
                                                                        color: isSelected ? '#fff' : '#475569',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        fontSize: '12px',
                                                                        fontWeight: '700'
                                                                    }}>
                                                                        {(u.name || u.username || 'A').charAt(0).toUpperCase()}
                                                                    </div>
                                                                    <div>
                                                                        <div style={{ fontWeight: '600', color: '#1e293b' }}>
                                                                            {u.name || u.username}
                                                                        </div>
                                                                        <div style={{ fontSize: '11px', color: '#64748b' }}>
                                                                            @{u.username} • {u.email}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td style={{ padding: '12px 10px' }}>
                                                                {hasPhone ? (
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                        <Smartphone size={14} color="#16a34a" />
                                                                        <span style={{ fontWeight: '600', color: '#15803d' }}>
                                                                            {u.userWhatsappNumber}
                                                                        </span>
                                                                        {u.userWhatsappVerified ? (
                                                                            <span style={{
                                                                                fontSize: '10px',
                                                                                fontWeight: '700',
                                                                                padding: '1px 6px',
                                                                                borderRadius: '4px',
                                                                                backgroundColor: '#dcfce7',
                                                                                color: '#15803d'
                                                                            }}>
                                                                                ✓ Verificado
                                                                            </span>
                                                                        ) : (
                                                                            <span style={{ fontSize: '10px', color: '#94a3b8' }}>
                                                                                (Pendente)
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                ) : (
                                                                    <span style={{
                                                                        display: 'inline-flex',
                                                                        alignItems: 'center',
                                                                        gap: '4px',
                                                                        fontSize: '11px',
                                                                        color: '#ef4444',
                                                                        backgroundColor: '#fef2f2',
                                                                        padding: '2px 8px',
                                                                        borderRadius: '4px',
                                                                        fontWeight: '600'
                                                                    }}>
                                                                        <AlertCircle size={12} /> Sem WhatsApp
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td style={{ padding: '12px 10px' }}>
                                                                <span style={{
                                                                    padding: '3px 8px',
                                                                    borderRadius: '6px',
                                                                    fontSize: '11px',
                                                                    fontWeight: '700',
                                                                    textTransform: 'uppercase',
                                                                    backgroundColor: u.plan === 'premium' ? '#fdf2f8' : (u.plan === 'basic' ? '#eff6ff' : (String(u.plan).endsWith('_divulgador') ? '#fef3c7' : '#f1f5f9')),
                                                                    color: u.plan === 'premium' ? '#db2777' : (u.plan === 'basic' ? '#3b82f6' : (String(u.plan).endsWith('_divulgador') ? '#b45309' : '#64748b'))
                                                                }}>
                                                                    {u.plan}
                                                                </span>
                                                            </td>
                                                            <td style={{ padding: '12px 10px', color: '#64748b', fontSize: '12px' }}>
                                                                {u.createdAt ? new Date(u.createdAt).toLocaleDateString('pt-BR') : '—'}
                                                            </td>
                                                            <td style={{ padding: '12px 10px', textAlign: 'center' }}>
                                                                {hasPhone ? (
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleToggleUserSelect(u.id);
                                                                        }}
                                                                        style={{
                                                                            padding: '4px 10px',
                                                                            borderRadius: '6px',
                                                                            border: isSelected ? '1px solid #c084fc' : '1px solid #cbd5e1',
                                                                            backgroundColor: isSelected ? '#9333ea' : '#fff',
                                                                            color: isSelected ? '#fff' : '#475569',
                                                                            fontSize: '11px',
                                                                            fontWeight: '700',
                                                                            cursor: 'pointer',
                                                                            transition: 'all 0.15s'
                                                                        }}
                                                                    >
                                                                        {isSelected ? '✓ Selecionado' : '+ Selecionar'}
                                                                    </button>
                                                                ) : (
                                                                    <span style={{ fontSize: '11px', color: '#cbd5e1', fontStyle: 'italic' }}>
                                                                        Indisponível
                                                                    </span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* SUB-ABA 2: DISPARO EM GRUPOS (EXISTENTE) */}
                    {broadcastSubTab === 'groups' && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '30px', alignItems: 'start' }}>
                            {/* Left Column: Form & Group Selection */}
                            <div style={{ background: 'white', padding: '25px', borderRadius: '20px', boxShadow: '0 4px 15px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                                    <div style={{ backgroundColor: '#f5f3ff', padding: '8px', borderRadius: '10px' }}>
                                        <Megaphone size={20} color="#9333ea" />
                                    </div>
                                    <h3 style={{ margin: 0, fontSize: '16px', color: '#1e293b' }}>Nova Transmissão para Grupos</h3>
                                </div>

                                <form onSubmit={handleSendBroadcast} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                    <div className="form-group">
                                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Instância de WhatsApp para Envio</label>
                                        <select
                                            value={broadcastInstance}
                                            onChange={(e) => setBroadcastInstance(e.target.value)}
                                            style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none', backgroundColor: 'white' }}
                                            disabled={sendingBroadcast}
                                        >
                                            <option value="A">Instância A (Marketing / Divulgação)</option>
                                            <option value="B">Instância B (Suporte / Sistema)</option>
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                            <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>Mensagem a ser Transmitida</label>
                                            <button
                                                type="button"
                                                onClick={() => setBroadcastMessage(DEFAULT_BROADCAST_TEMPLATE)}
                                                style={{ background: 'none', border: 'none', color: '#9333ea', fontSize: '12px', fontWeight: '600', cursor: 'pointer', padding: 0 }}
                                            >
                                                Restaurar Modelo
                                            </button>
                                        </div>
                                        <textarea
                                            value={broadcastMessage}
                                            onChange={(e) => setBroadcastMessage(e.target.value)}
                                            rows={8}
                                            style={{ width: '100%', padding: '12px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '13px', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}
                                            placeholder="Digite sua mensagem de transmissão..."
                                            disabled={sendingBroadcast}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                            <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                                                Grupos de Destino ({selectedGroups.length}/{broadcastGroups.length})
                                            </label>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <button
                                                    type="button"
                                                    onClick={() => handleSelectAllFiltered(filteredGroups.map(g => g.whatsappId))}
                                                    style={{ background: 'none', border: 'none', color: '#9333ea', fontSize: '12px', fontWeight: '600', cursor: 'pointer', padding: 0 }}
                                                    disabled={sendingBroadcast}
                                                >
                                                    Marcar Todos
                                                </button>
                                                <span style={{ color: '#cbd5e1' }}>|</span>
                                                <button
                                                    type="button"
                                                    onClick={() => handleDeselectAllFiltered(filteredGroups.map(g => g.whatsappId))}
                                                    style={{ background: 'none', border: 'none', color: '#64748b', fontSize: '12px', fontWeight: '600', cursor: 'pointer', padding: 0 }}
                                                    disabled={sendingBroadcast}
                                                >
                                                    Desmarcar
                                                </button>
                                            </div>
                                        </div>

                                        <div style={{ position: 'relative', marginBottom: '10px' }}>
                                            <Search size={16} color="#94a3b8" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                                            <input
                                                type="text"
                                                placeholder="Buscar por nome do grupo ou ID..."
                                                value={searchGroupQuery}
                                                onChange={(e) => setSearchGroupQuery(e.target.value)}
                                                style={{ width: '100%', padding: '8px 12px 8px 36px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }}
                                                disabled={sendingBroadcast}
                                            />
                                        </div>

                                        <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                            {loadingGroups ? (
                                                <div style={{ textAlign: 'center', padding: '20px', color: '#64748b', fontSize: '13px' }}>Carregando grupos...</div>
                                            ) : filteredGroups.length === 0 ? (
                                                <div style={{ textAlign: 'center', padding: '20px', color: '#64748b', fontSize: '13px' }}>Nenhum grupo encontrado.</div>
                                            ) : (
                                                filteredGroups.map(grupo => {
                                                    const isSelected = selectedGroups.includes(grupo.whatsappId);
                                                    return (
                                                        <div
                                                            key={grupo.id}
                                                            onClick={() => !sendingBroadcast && handleToggleSelectGroup(grupo.whatsappId)}
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                padding: '8px 12px',
                                                                borderRadius: '8px',
                                                                backgroundColor: isSelected ? '#f5f3ff' : 'transparent',
                                                                border: isSelected ? '1px solid #c084fc' : '1px solid transparent',
                                                                cursor: sendingBroadcast ? 'not-allowed' : 'pointer',
                                                                transition: '0.15s'
                                                            }}
                                                        >
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', overflow: 'hidden' }}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isSelected}
                                                                    onChange={() => {}}
                                                                    disabled={sendingBroadcast}
                                                                    style={{ cursor: 'pointer', accentColor: '#9333ea' }}
                                                                />
                                                                <span style={{ fontSize: '13px', fontWeight: isSelected ? '600' : '400', color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                    {grupo.nome}
                                                                </span>
                                                            </div>
                                                            <span style={{
                                                                fontSize: '11px',
                                                                padding: '2px 6px',
                                                                borderRadius: '4px',
                                                                backgroundColor: grupo.ativo ? '#dcfce7' : '#f1f5f9',
                                                                color: grupo.ativo ? '#15803d' : '#475569',
                                                                fontWeight: 'bold'
                                                            }}>
                                                                {grupo.ativo ? 'Ativo' : 'Inativo'}
                                                            </span>
                                                        </div>
                                                    );
                                                })
                                            )}
                                        </div>
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={sendingBroadcast || selectedGroups.length === 0}
                                        style={{
                                            backgroundColor: sendingBroadcast || selectedGroups.length === 0 ? '#cbd5e1' : '#9333ea',
                                            color: 'white',
                                            padding: '12px 20px',
                                            borderRadius: '10px',
                                            border: 'none',
                                            fontWeight: '600',
                                            fontSize: '14px',
                                            cursor: sendingBroadcast || selectedGroups.length === 0 ? 'not-allowed' : 'pointer',
                                            transition: '0.2s',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '8px'
                                        }}
                                    >
                                        <Play size={16} /> Disparar para {selectedGroups.length} {selectedGroups.length === 1 ? 'Grupo' : 'Grupos'}
                                    </button>
                                </form>
                            </div>

                            {/* Right Column: Status & Logs */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
                                {/* Broadcast Status Card */}
                                <div style={{ background: 'white', padding: '25px', borderRadius: '20px', boxShadow: '0 4px 15px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                        <h3 style={{ margin: 0, fontSize: '15px', color: '#1e293b' }}>Status da Transmissão</h3>
                                        {sendingBroadcast && (
                                            <span style={{ fontSize: '12px', color: '#9333ea', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <RefreshCw size={12} className="custom-spin" /> Em andamento
                                            </span>
                                        )}
                                    </div>

                                    {sendingBroadcast && broadcastStatus ? (
                                        <div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#475569', marginBottom: '8px', fontWeight: '500' }}>
                                                <span>Progresso de Envio:</span>
                                                <span>{broadcastStatus.current} de {broadcastStatus.total}</span>
                                            </div>
                                            <div style={{ width: '100%', height: '8px', backgroundColor: '#f1f5f9', borderRadius: '4px', overflow: 'hidden', marginBottom: '15px' }}>
                                                <div
                                                    style={{
                                                        height: '100%',
                                                        backgroundColor: '#9333ea',
                                                        width: `${Math.round((broadcastStatus.current / (broadcastStatus.total || 1)) * 100)}%`,
                                                        transition: '0.3s'
                                                    }}
                                                />
                                            </div>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', textAlign: 'center' }}>
                                                <div style={{ background: '#f0fdf4', padding: '10px', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                                                    <span style={{ fontSize: '11px', color: '#15803d', fontWeight: '600' }}>Sucesso</span>
                                                    <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#16a34a' }}>{broadcastStatus.sucessos}</div>
                                                </div>
                                                <div style={{ background: '#fef2f2', padding: '10px', borderRadius: '8px', border: '1px solid #fecaca' }}>
                                                    <span style={{ fontSize: '11px', color: '#b91c1c', fontWeight: '600' }}>Falhas</span>
                                                    <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#dc2626' }}>{broadcastStatus.falhas}</div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>
                                            <Clock size={32} style={{ color: '#cbd5e1', marginBottom: '8px' }} />
                                            <p style={{ margin: 0, fontSize: '13px' }}>Nenhum disparo em andamento no momento.</p>
                                        </div>
                                    )}
                                </div>

                                {/* Historical Logs Card */}
                                <div style={{ background: 'white', padding: '25px', borderRadius: '20px', boxShadow: '0 4px 15px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                        <h3 style={{ margin: 0, fontSize: '15px', color: '#1e293b' }}>Histórico de Transmissões (Grupos)</h3>
                                        <button
                                            type="button"
                                            onClick={fetchBroadcastLogs}
                                            disabled={loadingLogs}
                                            style={{ background: 'transparent', border: 'none', color: '#9333ea', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                                        >
                                            <RefreshCw size={14} className={loadingLogs ? "custom-spin" : ""} /> Atualizar
                                        </button>
                                    </div>

                                    {loadingLogs ? (
                                        <div style={{ display: 'flex', justifyContent: 'center', padding: '45px' }}>
                                            <RefreshCw className="custom-spin" size={32} style={{ color: '#9333ea' }} />
                                        </div>
                                    ) : broadcastLogs.length === 0 ? (
                                        <div style={{ textAlign: 'center', padding: '50px 20px', color: '#64748b' }}>
                                            <Megaphone size={40} style={{ color: '#cbd5e1', marginBottom: '15px' }} />
                                            <p style={{ margin: 0, fontSize: '14px', fontWeight: '500' }}>Nenhuma transmissão cadastrada.</p>
                                        </div>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '450px', overflowY: 'auto' }}>
                                            {broadcastLogs.map((log) => (
                                                <div
                                                    key={log.id}
                                                    style={{
                                                        background: '#f8fafc',
                                                        padding: '15px',
                                                        borderRadius: '12px',
                                                        border: '1px solid #e2e8f0'
                                                    }}
                                                >
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                                        <span style={{
                                                            fontSize: '11px',
                                                            backgroundColor: log.status === 'sucesso' ? '#dcfce7' : '#fee2e2',
                                                            color: log.status === 'sucesso' ? '#15803d' : '#b91c1c',
                                                            fontWeight: 'bold',
                                                            padding: '3px 8px',
                                                            borderRadius: '6px'
                                                        }}>
                                                            {log.status === 'sucesso' ? 'Completo' : 'Com falhas'}
                                                        </span>
                                                        <span style={{ fontSize: '11px', color: '#64748b' }}>
                                                            {new Date(log.createdAt).toLocaleDateString('pt-BR')} {new Date(log.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                                        </span>
                                                    </div>
                                                    <p style={{ margin: 0, fontSize: '13px', color: '#334155', fontWeight: '500', lineHeight: '1.4' }}>
                                                        {log.detalhes}
                                                    </p>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginTop: '8px', fontSize: '11px', color: '#64748b' }}>
                                                        <span>Destinos com sucesso: <strong>{log.quantidadeGrupos}</strong></span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* SUB-ABA 3: DISPARO DE E-MAIL MARKETING (RESEND) */}
                    {broadcastSubTab === 'email' && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '28px', alignItems: 'start' }}>
                            {/* Coluna Esquerda: Configuração da Campanha de E-mail */}
                            <div style={{ background: 'white', padding: '24px', borderRadius: '20px', boxShadow: '0 4px 15px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '18px' }}>
                                    <div style={{ backgroundColor: '#e0f2fe', padding: '8px', borderRadius: '10px' }}>
                                        <Mail size={20} color="#0284c7" />
                                    </div>
                                    <div>
                                        <h3 style={{ margin: 0, fontSize: '16px', color: '#1e293b' }}>Campanha de E-mail Marketing</h3>
                                        <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>Disparo com alta entregabilidade via Resend (@anunciosapp.com.br)</p>
                                    </div>
                                </div>

                                <form onSubmit={handleStartEmailBroadcast} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                    {/* Modelos Prontos de E-mail */}
                                    <div>
                                        <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                            Modelos Rápidos de E-mail
                                        </label>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '8px' }}>
                                            {USER_EMAIL_TEMPLATES.map((tmpl) => (
                                                <button
                                                    key={tmpl.id}
                                                    type="button"
                                                    onClick={() => {
                                                        setEmailBroadcastSubject(tmpl.subject);
                                                        setEmailBroadcastMessage(tmpl.text);
                                                        if (tmpl.id === 'unverified_activation') {
                                                            setEmailBroadcastFilter('unverified_whatsapp');
                                                        }
                                                    }}
                                                    style={{
                                                        padding: '10px 12px',
                                                        borderRadius: '8px',
                                                        border: '1px solid #e2e8f0',
                                                        backgroundColor: '#f8fafc',
                                                        color: '#334155',
                                                        fontSize: '12px',
                                                        fontWeight: '600',
                                                        cursor: 'pointer',
                                                        textAlign: 'left',
                                                        transition: 'all 0.15s',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.borderColor = '#0284c7';
                                                        e.currentTarget.style.backgroundColor = '#f0f9ff';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.borderColor = '#e2e8f0';
                                                        e.currentTarget.style.backgroundColor = '#f8fafc';
                                                    }}
                                                >
                                                    <span>{tmpl.title}</span>
                                                    <span style={{ fontSize: '11px', color: '#0284c7', fontWeight: '700' }}>Carregar ↵</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Segmentação do Público */}
                                    <div>
                                        <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                                            Público-Alvo da Campanha
                                        </label>
                                        <select
                                            value={emailBroadcastFilter}
                                            onChange={(e) => setEmailBroadcastFilter(e.target.value)}
                                            style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', outline: 'none', backgroundColor: '#fff' }}
                                            disabled={sendingEmailBroadcast}
                                        >
                                            <option value="unverified_whatsapp">🚨 Contas sem WhatsApp confirmado (Onboarding)</option>
                                            <option value="all">👥 Todos os usuários cadastrados</option>
                                            <option value="free">🎁 Apenas usuários no Plano Free</option>
                                            <option value="recent_7_days">📅 Recém-cadastrados (Últimos 7 dias)</option>
                                        </select>
                                    </div>

                                    {/* Assunto do E-mail */}
                                    <div>
                                        <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                                            Assunto do E-mail (Subject)
                                        </label>
                                        <input
                                            type="text"
                                            value={emailBroadcastSubject}
                                            onChange={(e) => setEmailBroadcastSubject(e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '10px 12px',
                                                borderRadius: '8px',
                                                border: '1px solid #cbd5e1',
                                                fontSize: '13px',
                                                outline: 'none',
                                                boxSizing: 'border-box'
                                            }}
                                            placeholder="Ex: 🚀 Complete seu cadastro no Anúncios App"
                                            disabled={sendingEmailBroadcast}
                                        />
                                    </div>

                                    {/* Corpo da Mensagem com Tags Dinâmicas */}
                                    <div>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                                            <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569' }}>
                                                Mensagem do E-mail
                                            </label>
                                            <div style={{ display: 'flex', gap: '4px' }}>
                                                {['{nome}', '{username}'].map((tag) => (
                                                    <button
                                                        key={tag}
                                                        type="button"
                                                        onClick={() => setEmailBroadcastMessage(prev => prev + ' ' + tag)}
                                                        style={{
                                                            padding: '2px 6px',
                                                            borderRadius: '4px',
                                                            border: '1px solid #cbd5e1',
                                                            backgroundColor: '#f1f5f9',
                                                            color: '#64748b',
                                                            fontSize: '11px',
                                                            cursor: 'pointer',
                                                            fontWeight: '600'
                                                        }}
                                                        title={`Inserir ${tag}`}
                                                    >
                                                        {tag}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <textarea
                                            value={emailBroadcastMessage}
                                            onChange={(e) => setEmailBroadcastMessage(e.target.value)}
                                            rows={6}
                                            style={{
                                                width: '100%',
                                                padding: '12px',
                                                borderRadius: '10px',
                                                border: '1px solid #cbd5e1',
                                                fontSize: '13px',
                                                lineHeight: '1.5',
                                                outline: 'none',
                                                resize: 'vertical',
                                                boxSizing: 'border-box',
                                                fontFamily: 'inherit'
                                            }}
                                            placeholder="Digite o texto do e-mail..."
                                            disabled={sendingEmailBroadcast}
                                        />
                                    </div>

                                    {/* Pré-visualização do E-mail */}
                                    <div>
                                        <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                                            Pré-visualização (Simulação de Caixa de Entrada)
                                        </label>
                                        <div style={{
                                            backgroundColor: '#f8fafc',
                                            borderRadius: '12px',
                                            border: '1px solid #e2e8f0',
                                            padding: '16px',
                                            fontSize: '13px'
                                        }}>
                                            <div style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '10px', marginBottom: '12px', fontSize: '12px', color: '#64748b' }}>
                                                <div><strong>De:</strong> Anúncios App &lt;nao-responda@anunciosapp.com.br&gt;</div>
                                                <div><strong>Assunto:</strong> {emailBroadcastSubject || '(Sem assunto)'}</div>
                                            </div>
                                            <div style={{
                                                backgroundColor: '#ffffff',
                                                padding: '16px',
                                                borderRadius: '8px',
                                                border: '1px solid #e2e8f0',
                                                whiteSpace: 'pre-wrap',
                                                lineHeight: '1.6',
                                                color: '#1e293b'
                                            }}>
                                                {emailBroadcastMessage
                                                    .replace(/{nome}/gi, 'Vinícius')
                                                    .replace(/{username}/gi, 'viniciusa')}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Teste antes de disparar */}
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        <input
                                            type="email"
                                            placeholder="Seu e-mail de teste (ex: seuemail@gmail.com)"
                                            value={emailBroadcastTestEmail}
                                            onChange={(e) => setEmailBroadcastTestEmail(e.target.value)}
                                            style={{ flex: 1, padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '12px', outline: 'none' }}
                                            disabled={sendingEmailBroadcast || testingEmailMessage}
                                        />
                                        <button
                                            type="button"
                                            onClick={handleSendTestEmail}
                                            disabled={sendingEmailBroadcast || testingEmailMessage}
                                            style={{
                                                padding: '10px 14px',
                                                borderRadius: '8px',
                                                border: '1px solid #0284c7',
                                                backgroundColor: '#f0f9ff',
                                                color: '#0284c7',
                                                fontSize: '12px',
                                                fontWeight: '700',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            <Mail size={15} /> Testar Envio
                                        </button>
                                    </div>

                                    {/* Botão Principal de Disparo */}
                                    <button
                                        type="submit"
                                        disabled={sendingEmailBroadcast}
                                        style={{
                                            backgroundColor: sendingEmailBroadcast ? '#cbd5e1' : '#0284c7',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '10px',
                                            padding: '14px',
                                            fontSize: '14px',
                                            fontWeight: '700',
                                            cursor: sendingEmailBroadcast ? 'not-allowed' : 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '8px',
                                            boxShadow: '0 4px 12px rgba(2, 132, 199, 0.3)',
                                            transition: 'background 0.2s'
                                        }}
                                    >
                                        <Send size={18} />
                                        <span>Iniciar Disparo de E-mails via Resend</span>
                                    </button>
                                </form>
                            </div>

                            {/* Coluna Direita: Progresso ao Vivo e Histórico */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                {/* Painel de Progresso ao Vivo */}
                                <div style={{ background: 'white', padding: '24px', borderRadius: '20px', boxShadow: '0 4px 15px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                        <h3 style={{ margin: 0, fontSize: '15px', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <RefreshCw size={17} color="#0284c7" className={emailBroadcastStatus?.status === 'running' ? "custom-spin" : ""} />
                                            Status do Disparador de E-mails
                                        </h3>
                                        <span style={{
                                            padding: '4px 10px',
                                            borderRadius: '6px',
                                            fontSize: '11px',
                                            fontWeight: '700',
                                            textTransform: 'uppercase',
                                            backgroundColor: emailBroadcastStatus?.status === 'running' ? '#fef3c7' : '#dcfce7',
                                            color: emailBroadcastStatus?.status === 'running' ? '#b45309' : '#15803d'
                                        }}>
                                            {emailBroadcastStatus?.status === 'running' ? 'Disparo em Andamento' : 'Ocioso / Pronto'}
                                        </span>
                                    </div>

                                    {emailBroadcastStatus?.status === 'running' ? (
                                        <div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                                                <span>Progresso: {emailBroadcastStatus.current} de {emailBroadcastStatus.total} e-mails</span>
                                                <span>{Math.round((emailBroadcastStatus.current / (emailBroadcastStatus.total || 1)) * 100)}%</span>
                                            </div>

                                            {/* Barra de Progresso */}
                                            <div style={{ height: '10px', backgroundColor: '#f1f5f9', borderRadius: '5px', overflow: 'hidden', marginBottom: '14px' }}>
                                                <div style={{
                                                    height: '100%',
                                                    backgroundColor: '#0284c7',
                                                    width: `${Math.round((emailBroadcastStatus.current / (emailBroadcastStatus.total || 1)) * 100)}%`,
                                                    transition: 'width 0.4s ease'
                                                }} />
                                            </div>

                                            {emailBroadcastStatus.currentRecipient && (
                                                <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '14px' }}>
                                                    Enviando para: <strong>{emailBroadcastStatus.currentRecipient}</strong>
                                                </div>
                                            )}

                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
                                                <div style={{ backgroundColor: '#f0fdf4', padding: '10px', borderRadius: '8px', border: '1px solid #bbf7d0', textAlign: 'center' }}>
                                                    <span style={{ fontSize: '11px', color: '#15803d', fontWeight: '600' }}>Entregues</span>
                                                    <div style={{ fontSize: '18px', fontWeight: '800', color: '#16a34a' }}>{emailBroadcastStatus.sucessos}</div>
                                                </div>
                                                <div style={{ backgroundColor: '#fef2f2', padding: '10px', borderRadius: '8px', border: '1px solid #fecaca', textAlign: 'center' }}>
                                                    <span style={{ fontSize: '11px', color: '#b91c1c', fontWeight: '600' }}>Falhas</span>
                                                    <div style={{ fontSize: '18px', fontWeight: '800', color: '#dc2626' }}>{emailBroadcastStatus.falhas}</div>
                                                </div>
                                            </div>

                                            <button
                                                type="button"
                                                onClick={handleStopEmailBroadcast}
                                                style={{
                                                    width: '100%',
                                                    padding: '10px',
                                                    borderRadius: '8px',
                                                    border: '1px solid #ef4444',
                                                    backgroundColor: '#fff',
                                                    color: '#ef4444',
                                                    fontWeight: '700',
                                                    fontSize: '13px',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '6px'
                                                }}
                                            >
                                                <StopCircle size={16} /> Interromper Envio Agora
                                            </button>
                                        </div>
                                    ) : (
                                        <div style={{ textAlign: 'center', padding: '20px 10px', color: '#64748b' }}>
                                            <CheckCircle size={36} color="#0284c7" style={{ marginBottom: '10px' }} />
                                            <p style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>
                                                Disparador de E-mails Pronto
                                            </p>
                                            <p style={{ margin: '4px 0 0 0', fontSize: '12px' }}>
                                                Envio oficial através dos servidores do Resend com templates responsivos e links inteligentes.
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {/* Histórico de Campanhas de E-mail */}
                                <div style={{ background: 'white', padding: '24px', borderRadius: '20px', boxShadow: '0 4px 15px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                        <h3 style={{ margin: 0, fontSize: '15px', color: '#1e293b' }}>Histórico de Campanhas por E-mail</h3>
                                        <button
                                            type="button"
                                            onClick={fetchEmailBroadcastLogs}
                                            disabled={loadingEmailLogs}
                                            style={{ background: 'none', border: 'none', color: '#0284c7', fontSize: '12px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                                        >
                                            <RefreshCw size={13} className={loadingEmailLogs ? "custom-spin" : ""} /> Atualizar
                                        </button>
                                    </div>

                                    {loadingEmailLogs ? (
                                        <div style={{ textAlign: 'center', padding: '30px' }}>
                                            <RefreshCw size={24} color="#0284c7" className="custom-spin" />
                                        </div>
                                    ) : emailBroadcastLogs.length === 0 ? (
                                        <div style={{ textAlign: 'center', padding: '30px 10px', color: '#94a3b8', fontSize: '13px' }}>
                                            Nenhum disparo de e-mail registrado ainda.
                                        </div>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '350px', overflowY: 'auto' }}>
                                            {emailBroadcastLogs.map((log) => (
                                                <div
                                                    key={log.id}
                                                    style={{
                                                        backgroundColor: '#f8fafc',
                                                        border: '1px solid #e2e8f0',
                                                        borderRadius: '10px',
                                                        padding: '12px'
                                                    }}
                                                >
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                                        <span style={{
                                                            fontSize: '10px',
                                                            fontWeight: '700',
                                                            padding: '2px 6px',
                                                            borderRadius: '4px',
                                                            backgroundColor: log.status === 'sucesso' ? '#dcfce7' : '#fef3c7',
                                                            color: log.status === 'sucesso' ? '#15803d' : '#b45309',
                                                            textTransform: 'uppercase'
                                                        }}>
                                                            {log.status === 'sucesso' ? 'Sucesso' : 'Parcial'}
                                                        </span>
                                                        <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                                                            {new Date(log.createdAt).toLocaleDateString('pt-BR')} {new Date(log.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                                        </span>
                                                    </div>
                                                    <p style={{ margin: 0, fontSize: '12px', color: '#334155', lineHeight: '1.4' }}>
                                                        {log.detalhes}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
            {showConfirmModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(15, 23, 42, 0.65)',
                    backdropFilter: 'blur(4px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999
                }}>
                    <div style={{
                        background: 'white',
                        padding: '30px',
                        borderRadius: '24px',
                        maxWidth: '500px',
                        width: '90%',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                        border: '1px solid #e2e8f0',
                        fontFamily: 'Outfit, sans-serif'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                            <div style={{ backgroundColor: '#fef2f2', padding: '10px', borderRadius: '12px' }}>
                                <AlertTriangle size={24} color="#ef4444" />
                            </div>
                            <h3 style={{ margin: 0, fontSize: '18px', color: '#1e293b', fontWeight: '800' }}>Confirmar Transmissão</h3>
                        </div>

                        <p style={{ fontSize: '15px', color: '#475569', lineHeight: '1.6', margin: '0 0 20px 0' }}>
                            Você está prestes a enviar uma mensagem de divulgação para <strong>{selectedGroups.length}</strong> grupos selecionados utilizando a <strong>Instância {broadcastInstance}</strong>.
                        </p>

                        <div style={{
                            background: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: '12px',
                            padding: '15px',
                            marginBottom: '25px',
                            maxHeight: '150px',
                            overflowY: 'auto'
                        }}>
                            <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#64748b', textTransform: 'uppercase', marginBottom: '6px', letterSpacing: '0.05em' }}>Prévia da Mensagem:</div>
                            <p style={{ margin: 0, fontSize: '13px', color: '#334155', whiteSpace: 'pre-wrap', lineHeight: '1.5' }}>{broadcastMessage}</p>
                        </div>

                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                            <button
                                type="button"
                                onClick={() => setShowConfirmModal(false)}
                                style={{
                                    padding: '12px 20px',
                                    borderRadius: '12px',
                                    border: '1px solid #cbd5e1',
                                    background: 'white',
                                    color: '#475569',
                                    fontSize: '14px',
                                    fontWeight: '700',
                                    cursor: 'pointer',
                                    transition: 'background-color 0.15s'
                                }}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                onClick={executeBroadcast}
                                style={{
                                    padding: '12px 24px',
                                    borderRadius: '12px',
                                    border: 'none',
                                    background: '#9333ea',
                                    color: 'white',
                                    fontSize: '14px',
                                    fontWeight: '700',
                                    cursor: 'pointer',
                                    boxShadow: '0 4px 10px rgba(147, 51, 234, 0.2)',
                                    transition: 'background-color 0.15s'
                                }}
                            >
                                Confirmar Envio
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ABA: GERENCIAMENTO DE INSTÂNCIAS WHATSAPP (A E B) */}
            {activeTab === 'whatsapp_instances' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    {/* Header e Toggle Principal da Instância A */}
                    <div style={{
                        padding: '24px',
                        background: '#fff',
                        borderRadius: '20px',
                        border: '1px solid #e2e8f0',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '16px',
                        boxShadow: '0 4px 15px rgba(0,0,0,0.03)'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{ backgroundColor: '#f3e8ff', padding: '10px', borderRadius: '12px' }}>
                                <MessageCircle size={22} color="#9333ea" />
                            </div>
                            <div>
                                <h3 style={{ margin: 0, fontSize: '16px', color: '#1e293b' }}>
                                    Habilitar WhatsApp de Marketing (Instância A)
                                </h3>
                                <p style={{ fontSize: '12px', color: '#64748b', margin: '4px 0 0 0' }}>
                                    Se desativado, o robô não executará a Instância A (Marketing) em segundo plano, evitando a solicitação desnecessária de QR Code.
                                </p>
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={handleToggleMarketingActive}
                            style={{
                                position: 'relative',
                                width: '52px',
                                height: '28px',
                                borderRadius: '14px',
                                border: 'none',
                                cursor: 'pointer',
                                backgroundColor: whatsappMarketingActive ? '#10b981' : '#cbd5e1',
                                transition: '0.2s',
                                outline: 'none'
                            }}
                        >
                            <span style={{
                                position: 'absolute',
                                width: '20px',
                                height: '20px',
                                borderRadius: '50%',
                                backgroundColor: '#fff',
                                top: '4px',
                                left: whatsappMarketingActive ? '28px' : '4px',
                                transition: '0.2s',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                            }} />
                        </button>
                    </div>

                    {/* Grid com os Cards das Instâncias A e B */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '24px', alignItems: 'start' }}>
                        {/* CARD INSTÂNCIA A */}
                        <div style={{
                            background: '#fff',
                            borderRadius: '20px',
                            border: '1px solid #e2e8f0',
                            padding: '24px',
                            boxShadow: '0 4px 15px rgba(0,0,0,0.03)',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '20px'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <h3 style={{ margin: 0, fontSize: '15px', color: '#1e293b', fontWeight: '700' }}>
                                        Instância A (Marketing / Automação)
                                    </h3>
                                    <p style={{ margin: '2px 0 0 0', fontSize: '11px', color: '#64748b' }}>
                                        Utilizada para campanhas globais e transmissões de marketing
                                    </p>
                                </div>
                                <span style={{
                                    padding: '4px 10px',
                                    borderRadius: '6px',
                                    fontSize: '11px',
                                    fontWeight: '700',
                                    textTransform: 'uppercase',
                                    backgroundColor: !whatsappMarketingActive ? '#f1f5f9' : (whatsStatusA === 'Conectado' ? '#dcfce7' : '#fee2e2'),
                                    color: !whatsappMarketingActive ? '#64748b' : (whatsStatusA === 'Conectado' ? '#15803d' : '#b91c1c')
                                }}>
                                    {!whatsappMarketingActive ? 'Desativada' : whatsStatusA}
                                </span>
                            </div>

                            {!whatsappMarketingActive ? (
                                <div style={{
                                    padding: '40px 20px',
                                    background: '#f8fafc',
                                    borderRadius: '12px',
                                    border: '1px dashed #cbd5e1',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    textAlign: 'center'
                                }}>
                                    <div style={{ fontSize: '36px', marginBottom: '10px' }}>🔇</div>
                                    <h4 style={{ color: '#64748b', fontWeight: '700', margin: '0 0 6px 0', fontSize: '14px' }}>
                                        Instância A Desativada
                                    </h4>
                                    <p style={{ fontSize: '12px', color: '#94a3b8', margin: 0, maxWidth: '260px' }}>
                                        O WhatsApp de Marketing está desligado. Ative a chave acima para gerar o QR Code e conectar.
                                    </p>
                                </div>
                            ) : (
                                <div>
                                    {/* Caixa de Conexão / QR Code */}
                                    <div style={{
                                        backgroundColor: '#f8fafc',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '14px',
                                        padding: '24px',
                                        textAlign: 'center',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: '12px'
                                    }}>
                                        {qrCodeA ? (
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                                                <div style={{ backgroundColor: '#fff', padding: '10px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                                                    <QRCodeCanvas value={qrCodeA} size={190} />
                                                </div>
                                                <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>
                                                    Escaneie o QR Code acima no WhatsApp para conectar.
                                                </p>
                                            </div>
                                        ) : whatsStatusA === 'Conectado' ? (
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                                                <div style={{ fontSize: '36px' }}>🟢</div>
                                                <div style={{ fontWeight: '700', color: '#15803d', fontSize: '15px' }}>
                                                    Instância A Conectada
                                                </div>
                                                <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>
                                                    O número está ativo e pronto para transmissões automáticas.
                                                </p>
                                                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleLogoutAdmin('A')}
                                                        style={{
                                                            padding: '8px 14px',
                                                            borderRadius: '8px',
                                                            border: '1px solid #fee2e2',
                                                            backgroundColor: '#fef2f2',
                                                            color: '#dc2626',
                                                            fontSize: '12px',
                                                            fontWeight: '700',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '6px'
                                                        }}
                                                    >
                                                        <LogOut size={14} /> Desconectar
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRestartWhatsapp('A')}
                                                        style={{
                                                            padding: '8px 14px',
                                                            borderRadius: '8px',
                                                            border: '1px solid #cbd5e1',
                                                            backgroundColor: '#fff',
                                                            color: '#475569',
                                                            fontSize: '12px',
                                                            fontWeight: '600',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '6px'
                                                        }}
                                                    >
                                                        <RefreshCw size={14} /> Reiniciar
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                                                <div style={{ fontSize: '36px' }}>🔴</div>
                                                <div style={{ fontWeight: '700', color: '#dc2626', fontSize: '15px' }}>
                                                    Instância Desconectada
                                                </div>
                                                <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>
                                                    Aguardando geração do QR Code ou motor reiniciando.
                                                </p>
                                                <button
                                                    type="button"
                                                    onClick={() => handleRestartWhatsapp('A')}
                                                    style={{
                                                        marginTop: '6px',
                                                        padding: '8px 16px',
                                                        borderRadius: '8px',
                                                        border: 'none',
                                                        backgroundColor: '#9333ea',
                                                        color: '#fff',
                                                        fontSize: '12px',
                                                        fontWeight: '700',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '6px'
                                                    }}
                                                >
                                                    <RefreshCw size={14} /> Forçar Novo QR Code
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    {/* Configurações Avançadas da Instância A */}
                                    <div style={{ marginTop: '20px', borderTop: '1px solid #f1f5f9', paddingTop: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                        {/* Auto-Resposta IA */}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div>
                                                <strong style={{ fontSize: '13px', color: '#1e293b' }}>Auto-Resposta IA</strong>
                                                <p style={{ fontSize: '11px', color: '#64748b', margin: '2px 0 0 0' }}>Responder conversas privadas automaticamente com IA</p>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => setWhatsappAutoReplyA(!whatsappAutoReplyA)}
                                                style={{
                                                    position: 'relative',
                                                    width: '44px',
                                                    height: '24px',
                                                    borderRadius: '12px',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    backgroundColor: whatsappAutoReplyA ? '#10b981' : '#cbd5e1',
                                                    transition: '0.2s'
                                                }}
                                            >
                                                <span style={{
                                                    position: 'absolute',
                                                    width: '16px',
                                                    height: '16px',
                                                    borderRadius: '50%',
                                                    backgroundColor: '#fff',
                                                    top: '4px',
                                                    left: whatsappAutoReplyA ? '24px' : '4px',
                                                    transition: '0.2s'
                                                }} />
                                            </button>
                                        </div>

                                        {/* Prompt IA */}
                                        {whatsappAutoReplyA && (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <label style={{ fontSize: '12px', fontWeight: '700', color: '#475569' }}>Prompt do Sistema (IA)</label>
                                                    <button
                                                        type="button"
                                                        onClick={() => loadExamplePrompt('A')}
                                                        style={{ background: 'none', border: 'none', color: '#9333ea', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}
                                                    >
                                                        ✨ Carregar Exemplo
                                                    </button>
                                                </div>
                                                <textarea
                                                    rows={5}
                                                    value={aiPromptA}
                                                    onChange={(e) => setAiPromptA(e.target.value)}
                                                    placeholder="Instruções para a IA da Instância A..."
                                                    style={{
                                                        width: '100%',
                                                        padding: '10px',
                                                        borderRadius: '8px',
                                                        border: '1px solid #cbd5e1',
                                                        fontSize: '12px',
                                                        fontFamily: 'monospace',
                                                        outline: 'none',
                                                        resize: 'vertical',
                                                        boxSizing: 'border-box'
                                                    }}
                                                />
                                            </div>
                                        )}

                                        {/* Postar no Status */}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #f1f5f9', paddingTop: '12px' }}>
                                            <div>
                                                <strong style={{ fontSize: '13px', color: '#1e293b' }}>Postar no Status</strong>
                                                <p style={{ fontSize: '11px', color: '#64748b', margin: '2px 0 0 0' }}>Postar ofertas automaticamente no Status do WhatsApp</p>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => setPostarNoStatus(!postarNoStatus)}
                                                style={{
                                                    position: 'relative',
                                                    width: '44px',
                                                    height: '24px',
                                                    borderRadius: '12px',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    backgroundColor: postarNoStatus ? '#10b981' : '#cbd5e1',
                                                    transition: '0.2s'
                                                }}
                                            >
                                                <span style={{
                                                    position: 'absolute',
                                                    width: '16px',
                                                    height: '16px',
                                                    borderRadius: '50%',
                                                    backgroundColor: '#fff',
                                                    top: '4px',
                                                    left: postarNoStatus ? '24px' : '4px',
                                                    transition: '0.2s'
                                                }} />
                                            </button>
                                        </div>

                                        {/* Botão Salvar Configs da Instância A */}
                                        <button
                                            type="button"
                                            onClick={() => handleSaveInstanceSettings('A')}
                                            disabled={savingInstanceConfig}
                                            style={{
                                                padding: '10px',
                                                borderRadius: '8px',
                                                border: 'none',
                                                backgroundColor: '#9333ea',
                                                color: '#fff',
                                                fontSize: '12px',
                                                fontWeight: '700',
                                                cursor: savingInstanceConfig ? 'not-allowed' : 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '6px'
                                            }}
                                        >
                                            <Save size={14} /> Salvar Configurações da Instância A
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* CARD INSTÂNCIA B */}
                        <div style={{
                            background: '#fff',
                            borderRadius: '20px',
                            border: '1px solid #e2e8f0',
                            padding: '24px',
                            boxShadow: '0 4px 15px rgba(0,0,0,0.03)',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '20px'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <h3 style={{ margin: 0, fontSize: '15px', color: '#1e293b', fontWeight: '700' }}>
                                        Instância B (Suporte / Sistema)
                                    </h3>
                                    <p style={{ margin: '2px 0 0 0', fontSize: '11px', color: '#64748b' }}>
                                        Utilizada para disparos avulsos para afiliados cadastrados e suporte
                                    </p>
                                </div>
                                <span style={{
                                    padding: '4px 10px',
                                    borderRadius: '6px',
                                    fontSize: '11px',
                                    fontWeight: '700',
                                    textTransform: 'uppercase',
                                    backgroundColor: whatsStatusB === 'Conectado' ? '#dcfce7' : '#fee2e2',
                                    color: whatsStatusB === 'Conectado' ? '#15803d' : '#b91c1c'
                                }}>
                                    {whatsStatusB}
                                </span>
                            </div>

                            {/* Caixa de Conexão / QR Code */}
                            <div style={{
                                backgroundColor: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                borderRadius: '14px',
                                padding: '24px',
                                textAlign: 'center',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '12px'
                            }}>
                                {qrCodeB ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                                        <div style={{ backgroundColor: '#fff', padding: '10px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                                            <QRCodeCanvas value={qrCodeB} size={190} />
                                        </div>
                                        <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>
                                            Escaneie o QR Code acima no WhatsApp para conectar a Instância B.
                                        </p>
                                    </div>
                                ) : whatsStatusB === 'Conectado' ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                                        <div style={{ fontSize: '36px' }}>🟢</div>
                                        <div style={{ fontWeight: '700', color: '#15803d', fontSize: '15px' }}>
                                            Instância B Conectada
                                        </div>
                                        <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>
                                            O número está ativo e pronto para disparos avulsos e suporte aos afiliados.
                                        </p>
                                        <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                                            <button
                                                type="button"
                                                onClick={() => handleLogoutAdmin('B')}
                                                style={{
                                                    padding: '8px 14px',
                                                    borderRadius: '8px',
                                                    border: '1px solid #fee2e2',
                                                    backgroundColor: '#fef2f2',
                                                    color: '#dc2626',
                                                    fontSize: '12px',
                                                    fontWeight: '700',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px'
                                                }}
                                            >
                                                <LogOut size={14} /> Desconectar
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleRestartWhatsapp('B')}
                                                style={{
                                                    padding: '8px 14px',
                                                    borderRadius: '8px',
                                                    border: '1px solid #cbd5e1',
                                                    backgroundColor: '#fff',
                                                    color: '#475569',
                                                    fontSize: '12px',
                                                    fontWeight: '600',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px'
                                                }}
                                            >
                                                <RefreshCw size={14} /> Reiniciar
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                                        <div style={{ fontSize: '36px' }}>🔴</div>
                                        <div style={{ fontWeight: '700', color: '#dc2626', fontSize: '15px' }}>
                                            Instância Desconectada
                                        </div>
                                        <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>
                                            Aguardando conexão. Clique abaixo para forçar novo QR Code.
                                        </p>
                                        <button
                                            type="button"
                                            onClick={() => handleRestartWhatsapp('B')}
                                            style={{
                                                marginTop: '6px',
                                                padding: '8px 16px',
                                                borderRadius: '8px',
                                                border: 'none',
                                                backgroundColor: '#9333ea',
                                                color: '#fff',
                                                fontSize: '12px',
                                                fontWeight: '700',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px'
                                            }}
                                        >
                                            <RefreshCw size={14} /> Forçar Novo QR Code
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Configurações Avançadas da Instância B */}
                            <div style={{ marginTop: '20px', borderTop: '1px solid #f1f5f9', paddingTop: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                {/* Auto-Resposta IA */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div>
                                        <strong style={{ fontSize: '13px', color: '#1e293b' }}>Auto-Resposta IA</strong>
                                        <p style={{ fontSize: '11px', color: '#64748b', margin: '2px 0 0 0' }}>Responder conversas privadas automaticamente com IA</p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setWhatsappAutoReplyB(!whatsappAutoReplyB)}
                                        style={{
                                            position: 'relative',
                                            width: '44px',
                                            height: '24px',
                                            borderRadius: '12px',
                                            border: 'none',
                                            cursor: 'pointer',
                                            backgroundColor: whatsappAutoReplyB ? '#10b981' : '#cbd5e1',
                                            transition: '0.2s'
                                        }}
                                    >
                                        <span style={{
                                            position: 'absolute',
                                            width: '16px',
                                            height: '16px',
                                            borderRadius: '50%',
                                            backgroundColor: '#fff',
                                            top: '4px',
                                            left: whatsappAutoReplyB ? '24px' : '4px',
                                            transition: '0.2s'
                                        }} />
                                    </button>
                                </div>

                                {/* Prompt IA */}
                                {whatsappAutoReplyB && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <label style={{ fontSize: '12px', fontWeight: '700', color: '#475569' }}>Prompt do Sistema (IA)</label>
                                            <button
                                                type="button"
                                                onClick={() => loadExamplePrompt('B')}
                                                style={{ background: 'none', border: 'none', color: '#9333ea', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}
                                            >
                                                ✨ Carregar Exemplo
                                            </button>
                                        </div>
                                        <textarea
                                            rows={5}
                                            value={aiPromptB}
                                            onChange={(e) => setAiPromptB(e.target.value)}
                                            placeholder="Instruções para a IA da Instância B..."
                                            style={{
                                                width: '100%',
                                                padding: '10px',
                                                borderRadius: '8px',
                                                border: '1px solid #cbd5e1',
                                                fontSize: '12px',
                                                fontFamily: 'monospace',
                                                outline: 'none',
                                                resize: 'vertical',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </div>
                                )}

                                {/* Botão Salvar Configs da Instância B */}
                                <button
                                    type="button"
                                    onClick={() => handleSaveInstanceSettings('B')}
                                    disabled={savingInstanceConfig}
                                    style={{
                                        padding: '10px',
                                        borderRadius: '8px',
                                        border: 'none',
                                        backgroundColor: '#9333ea',
                                        color: '#fff',
                                        fontSize: '12px',
                                        fontWeight: '700',
                                        cursor: savingInstanceConfig ? 'not-allowed' : 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '6px'
                                    }}
                                >
                                    <Save size={14} /> Salvar Configurações da Instância B
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'users' && (
                <div style={{ background: 'white', borderRadius: '20px', padding: '25px', boxShadow: '0 4px 15px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{ backgroundColor: '#f5f3ff', padding: '8px', borderRadius: '10px' }}>
                                <Users size={20} color="#9333ea" />
                            </div>
                            <div>
                                <h3 style={{ margin: 0, fontSize: '16px', color: '#1e293b' }}>Gerenciamento de Usuários & Equipe</h3>
                                <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>Total de {adminUsers.length} usuários registrados na plataforma</p>
                            </div>
                        </div>
                        <button
                            onClick={fetchAdminUsers}
                            disabled={loadingAdminUsers}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                color: '#9333ea',
                                fontSize: '13px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                            }}
                        >
                            <RefreshCw size={14} className={loadingAdminUsers ? "custom-spin" : ""} /> Atualizar Lista
                        </button>
                    </div>

                    {loadingAdminUsers ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '45px' }}>
                            <RefreshCw className="custom-spin" size={32} style={{ color: '#9333ea' }} />
                        </div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
                                <thead>
                                    <tr style={{ borderBottom: '2px solid #e2e8f0', color: '#64748b' }}>
                                        <th style={{ padding: '12px 10px' }}>Usuário / Email</th>
                                        <th style={{ padding: '12px 10px' }}>Cargo</th>
                                        <th style={{ padding: '12px 10px' }}>WhatsApp</th>
                                        <th style={{ padding: '12px 10px' }}>Plano</th>
                                        <th style={{ padding: '12px 10px' }}>Status Pagamento</th>
                                        <th style={{ padding: '12px 10px' }}>Data Assinatura</th>
                                        <th style={{ padding: '12px 10px' }}>Próxima Cobrança</th>
                                        <th style={{ padding: '12px 10px', textAlign: 'center' }}>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {adminUsers.map(user => {
                                        const roleConfig = {
                                            admin: { label: 'Admin', bg: '#f3e8ff', color: '#9333ea' },
                                            moderator: { label: 'Moderador', bg: '#fef3c7', color: '#b45309' },
                                            support: { label: 'Suporte', bg: '#dbeafe', color: '#1d4ed8' },
                                            user: { label: 'Usuário', bg: '#f1f5f9', color: '#475569' }
                                        }[user.role || 'user'] || { label: user.role, bg: '#f1f5f9', color: '#475569' };

                                        return (
                                            <tr key={user.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                <td style={{ padding: '12px 10px' }}>
                                                    <div style={{ fontWeight: '600', color: '#1e293b' }}>{user.username}</div>
                                                    <div style={{ fontSize: '11px', color: '#64748b' }}>{user.email}</div>
                                                </td>
                                                <td style={{ padding: '12px 10px' }}>
                                                    <span style={{
                                                        padding: '3px 8px',
                                                        borderRadius: '6px',
                                                        fontSize: '11px',
                                                        fontWeight: '700',
                                                        textTransform: 'uppercase',
                                                        backgroundColor: roleConfig.bg,
                                                        color: roleConfig.color
                                                    }}>
                                                        {roleConfig.label}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '12px 10px' }}>
                                                    {user.userWhatsappNumber ? (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#334155' }}>
                                                            <span>{user.userWhatsappNumber}</span>
                                                            {user.userWhatsappVerified ? (
                                                                <span title="WhatsApp Verificado" style={{ color: '#10b981', fontWeight: 'bold' }}>✓</span>
                                                            ) : (
                                                                <span title="Não verificado" style={{ color: '#94a3b8', fontSize: '10px' }}>(pendente)</span>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <span style={{ color: '#94a3b8', fontSize: '12px' }}>Não cadastrado</span>
                                                    )}
                                                </td>
                                                <td style={{ padding: '12px 10px' }}>
                                                    <span style={{ 
                                                        padding: '4px 8px', 
                                                        borderRadius: '6px', 
                                                        fontSize: '11px', 
                                                        fontWeight: '700',
                                                        textTransform: 'uppercase',
                                                        backgroundColor: user.plan === 'premium' ? '#fdf2f8' : (user.plan === 'basic' ? '#eff6ff' : '#f1f5f9'),
                                                        color: user.plan === 'premium' ? '#db2777' : (user.plan === 'basic' ? '#3b82f6' : '#64748b')
                                                    }}>
                                                        {user.plan}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '12px 10px' }}>
                                                    {user.plan !== 'free' && user.plan !== 'admin' ? (
                                                        user.cancel_at_period_end ? (
                                                            <span style={{ color: '#ef4444', fontWeight: '600' }}>Cancelado</span>
                                                        ) : (
                                                            <span style={{ color: '#10b981', fontWeight: '600' }}>Ativo (Em Dia)</span>
                                                        )
                                                    ) : (
                                                        <span style={{ color: '#94a3b8' }}>N/A</span>
                                                    )}
                                                </td>
                                                <td style={{ padding: '12px 10px', color: '#475569' }}>
                                                    {user.plan_signed_at ? new Date(user.plan_signed_at).toLocaleDateString('pt-BR') : '-'}
                                                </td>
                                                <td style={{ padding: '12px 10px', color: '#475569', fontWeight: '600' }}>
                                                    {user.planExpiresAt ? new Date(user.planExpiresAt).toLocaleDateString('pt-BR') : (user.expirationDate ? new Date(user.expirationDate).toLocaleDateString('pt-BR') : '-')}
                                                </td>
                                                <td style={{ padding: '12px 10px', textAlign: 'center' }}>
                                                    <button
                                                        onClick={() => {
                                                            setEditPlanData({
                                                                userId: user.id,
                                                                username: user.username,
                                                                plan: user.plan,
                                                                trialDays: '',
                                                                expirationMode: 'lifetime',
                                                                role: user.role || 'user',
                                                                initialRole: user.role || 'user'
                                                            });
                                                            setShowEditPlanModal(true);
                                                        }}
                                                        style={{ background: '#f3e8ff', color: '#9333ea', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
                                                    >
                                                        Gerenciar
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {showEditPlanModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
                    <div style={{ background: 'white', padding: '30px', borderRadius: '20px', width: '100%', maxWidth: '420px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}>
                        <h3 style={{ margin: '0 0 5px 0', color: '#1e293b' }}>Gerenciar Usuário</h3>
                        <p style={{ margin: '0 0 20px 0', fontSize: '13px', color: '#64748b' }}>Usuário: <strong>{editPlanData.username}</strong></p>
                        
                        <form onSubmit={handleUpdateUserPlan}>
                            {userPlan === 'admin' && (
                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>
                                        Cargo / Permissão (Role)
                                    </label>
                                    <select 
                                        value={editPlanData.role || 'user'}
                                        onChange={(e) => setEditPlanData({ ...editPlanData, role: e.target.value })}
                                        style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', backgroundColor: '#fff', fontSize: '13px' }}
                                    >
                                        <option value="user">Usuário (Padrão)</option>
                                        <option value="support">Suporte (Atendimento e Consulta)</option>
                                        <option value="moderator">Moderador (Gestão e Comunidade)</option>
                                        <option value="admin">Administrador Geral</option>
                                    </select>
                                </div>
                            )}

                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>Plano de Acesso</label>
                                <select 
                                    value={editPlanData.plan}
                                    onChange={(e) => setEditPlanData({ ...editPlanData, plan: e.target.value })}
                                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', backgroundColor: '#fff', fontSize: '13px' }}
                                >
                                    <option value="free">Free</option>
                                    <option value="basic">Basic</option>
                                    <option value="premium">Premium</option>
                                    <option value="free_divulgador">Free Divulgador</option>
                                    <option value="basic_divulgador">Basic Divulgador</option>
                                    <option value="premium_divulgador">Premium Divulgador</option>
                                </select>
                            </div>
                            
                            {editPlanData.plan !== 'free' && editPlanData.plan !== 'free_divulgador' && (
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>Duração do Plano</label>
                                    <select 
                                        value={editPlanData.expirationMode || 'lifetime'}
                                        onChange={(e) => {
                                            const mode = e.target.value;
                                            setEditPlanData({ ...editPlanData, expirationMode: mode, trialDays: mode === 'lifetime' ? '' : config.defaultTrialDays });
                                        }}
                                        style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', marginBottom: '10px', backgroundColor: '#fff', fontSize: '13px' }}
                                    >
                                        <option value="lifetime">Vitalício (Nunca expira)</option>
                                        <option value="temporary">Temporário (Expira em dias)</option>
                                    </select>

                                    {editPlanData.expirationMode === 'temporary' && (
                                        <div style={{ marginTop: '10px' }}>
                                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>Quantidade de Dias</label>
                                            <input 
                                                type="number"
                                                placeholder={`Ex: ${config.defaultTrialDays}`}
                                                value={editPlanData.trialDays}
                                                onChange={(e) => setEditPlanData({ ...editPlanData, trialDays: e.target.value })}
                                                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', boxSizing: 'border-box' }}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}

                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '24px' }}>
                                <button type="button" onClick={() => setShowEditPlanModal(false)} style={{ padding: '10px 16px', borderRadius: '8px', border: 'none', background: '#f1f5f9', color: '#475569', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}>Cancelar</button>
                                <button type="submit" style={{ padding: '10px 16px', borderRadius: '8px', border: 'none', background: '#9333ea', color: 'white', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}>Salvar Alterações</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminPage;
